/* Route Print Studio - storefront controller (vanilla, no framework).
 *
 * Talks to the Route Studio backend through the Shopify App Proxy (data-proxy),
 * which signs every request. The backend renders previews server-side and returns
 * the signed line-item properties used to add to basket. Raw route coordinates are
 * never sent to or held in the browser. */
(function () {
  'use strict';

  var root = document.querySelector('[data-rps]');
  if (!root) return;
  // Only the first instance is wired (global/document listeners would collide). A second
  // copy (e.g. dropped twice in the theme editor) would be inert; warn so that is not a mystery.
  if (document.querySelectorAll('[data-rps]').length > 1 && window.console) {
    console.warn('[route-studio] More than one Route Print Studio section on the page; only the first is interactive.');
  }

  var PROXY = (root.dataset.proxy || '/apps/route-studio').replace(/\/$/, '');
  var LOCKED = new Set((root.dataset.lockedStyles || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean));
  var DEFAULT_STYLE = root.dataset.defaultStyle || 'race-blueprint';
  var STRAVA_ENABLED = root.dataset.stravaEnabled === 'true';
  var STRAVA_START = (root.dataset.stravaStart || '').trim();
  // Draw-your-own-route: Stadia raster tiles authenticated by DOMAIN whitelist (no API
  // key in the browser; keyless on localhost for dev). Leaflet is vendored + lazy-loaded.
  var STADIA_TILES = 'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png';
  var STADIA_ATTR = '&copy; <a href="https://stadiamaps.com/" target="_blank" rel="noopener">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';
  var DRAW_LINE_COLOUR = '#c0512c'; // terracotta route line drawn over the light basemap
  var MAX_DRAW_POINTS = 50; // matches the backend cap (Valhalla's 50-location routing limit)
  var MAX_UPLOAD = 10 * 1024 * 1024;
  var STEP_ORDER = ['route', 'style', 'text', 'size', 'review'];
  // Forward-action label per step. The single primary button (in the fixed bottom bar)
  // shows these on steps 1-4 and "Add to basket" on Review. Style precedes Text: the
  // tap-only look choice (instant preview reward) comes before the keyboard data entry.
  var NEXT_LABELS = { route: 'Next: Choose style', style: 'Next: Add text', text: 'Next: Choose size', size: 'Next: Review' };
  var ADDED_TO_CART_MSG = 'Added to your basket. Add another and the second is 50% off, applied at checkout.';

  var SIZE_DIMS = { A1: '594 x 841 mm', A2: '420 x 594 mm', A3: '297 x 420 mm', A4: '210 x 297 mm' };
  var STYLE_NAMES = {
    minimal: 'Pure', 'race-blueprint': 'Midnight', 'heritage-atlas': 'Heritage',
    'parkland-map': 'Fresh', 'classic-atlas': 'Classic', 'terrain-map': 'Terrain',
    satellite: 'Satellite', outdoors: 'Outdoors', watercolour: 'Watercolour', 'medal-relief-photo': 'Medal Relief Photo'
  };
  var PRESENTATION_NAMES = { 'print-only': 'Print only', framed: 'Framed' };

  // Each style's OWN default route colour, mirrored from app/src/core/styles.mjs
  // (the server is the source of truth). Used only to seed/reset the colour picker
  // when the customer has not chosen a custom colour; the actual render always uses
  // the server style, so a drift here only affects the picker's starting swatch.
  var STYLE_ROUTE_COLOURS = {
    'race-blueprint': '#ff6b35', 'heritage-atlas': '#b9532b', 'parkland-map': '#d9542c',
    'classic-atlas': '#d2502a', minimal: '#1b2a3a', 'terrain-map': '#b3402a',
    satellite: '#ff6b35', outdoors: '#cf5a2b', watercolour: '#bf3f24', 'medal-relief-photo': '#d4af37'
  };
  function styleDefaultColour(id) { return STYLE_ROUTE_COLOURS[id] || '#1b2a3a'; }

  var product = parseJson(root.querySelector('[data-rps-product]'));
  // The "Digital copy" add-on is a SEPARATE £5 product/variant (so the print variants
  // stay exactly at the canonical Size x Frame matrix). When ticked it adds its price
  // to the subtotal and goes into the basket as a second line item. { variantId, price(pence) }.
  var DIGITAL = (product && product.digital) || {};

  var state = {
    jobId: null,
    jobPromise: null,
    currentStep: 'route',
    routeMetres: null,
    routeMovingSeconds: null,        // raw seconds from the import, so pace can re-derive on a unit switch
    routeElevationGainMetres: null,  // raw metres from the import, so elevation can re-derive on a unit switch
    edited: {}, // text fields the customer has manually typed in (by key); these survive a route switch
    stravaActivities: [],
    activityFilter: 'all',
    activitySearch: '',
    activitySort: 'recent',
    activityBadges: {},
    selectedActivityId: null,
    selectedPopularId: null,
    popularLoaded: null,
    popularSearch: '',
    drawMap: null,
    drawLayer: null,
    drawPins: [],
    drawMarkers: [],
    drawRawLine: null,
    drawSnapLine: null,
    drawSnapTimer: null,
    drawSnapToken: 0,
    drawPreviewReady: false, // a valid snapped line exists for the current pins
    drawLastKey: '',         // fingerprint of the last-snapped pin set (skip redundant snaps)
    drawLastDistance: null,  // metres from the last successful preview snap (for the readout)
    drawCommitting: false,
    drawTileErrors: 0,
    hasRoute: false,
    source: null, // route source (e.g. 'strava_activity'); drives the Powered by Strava credit
    previewRendered: false,
    previewFailed: false,
    previewToken: 0,
    previewAttempt: 0, // consecutive preview-render failures; drives the one silent auto-retry
    saving: false,
    pendingSave: false,
    addingToCart: false,
    addedToCart: false, // set once a print is in the basket; flips the bar to "Add another for 50% off"
    design: {
      styleId: DEFAULT_STYLE,
      routeColour: '', // '' = use the chosen style's own route colour; #rrggbb = custom override
      text: { title: '', subtitle: '', date: '', distance: '', distanceUnit: 'miles', elevation: '', finishTime: '', averagePace: '' },
      product: { size: 'A4', presentation: 'print-only', digitalCopy: false }
    }
  };

  // ---- element helpers ---------------------------------------------------
  function qs(sel, ctx) { return (ctx || root).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function parseJson(node) { try { return node ? JSON.parse(node.textContent) : {}; } catch (e) { return {}; } }
  function show(node) { if (node) node.hidden = false; }
  function hide(node) { if (node) node.hidden = true; }
  function setAllText(nodes, text) { nodes.forEach(function (n) { if (n) n.textContent = text; }); }

  var el = {
    panels: {},
    routeError: qs('[data-rps-route-error]'),
    routeNote: qs('[data-rps-route-note]'),
    routeSummary: qs('[data-rps-route-summary]'),
    routeSummaryText: qs('[data-rps-route-summary-text]'),
    unitHint: qs('[data-rps-unit-hint]'),
    gpxInput: qs('[data-rps-gpx-input]'),
    gpxStatus: qs('[data-rps-gpx-status]'),
    dropzone: qs('[data-rps-dropzone]'),
    stravaCredit: qs('[data-rps-strava-credit]'),
    divider: qs('[data-rps-divider]'),
    stravaPanel: qs('[data-rps-strava-panel]'),
    stravaList: qs('[data-rps-strava-list]'),
    stravaHint: qs('[data-rps-strava-hint]'),
    stravaEmpty: qs('[data-rps-strava-empty]'),
    stravaReconnect: qs('[data-rps-strava-reconnect]'),
    stravaTools: qs('[data-rps-strava-tools]'),
    stravaSearch: qs('[data-rps-strava-search]'),
    stravaFilters: qs('[data-rps-strava-filters]'),
    stravaSort: qs('[data-rps-strava-sort]'),
    stravaNoResults: qs('[data-rps-strava-noresults]'),
    stravaStatus: qs('[data-rps-strava-status]'),
    popularPanel: qs('[data-rps-popular-panel]'),
    popularList: qs('[data-rps-popular-list]'),
    popularSearch: qs('[data-rps-popular-search]'),
    popularNoResults: qs('[data-rps-popular-noresults]'),
    popularStatus: qs('[data-rps-popular-status]'),
    drawPanel: qs('[data-rps-draw-panel]'),
    drawMapEl: qs('[data-rps-draw-map]'),
    drawNudge: qs('[data-rps-draw-nudge]'),
    drawMapFail: qs('[data-rps-draw-mapfail]'),
    drawTools: qs('[data-rps-draw-tools]'),
    drawLocate: qs('[data-rps-draw-locate]'),
    drawAdd: qs('[data-rps-draw-add]'),
    drawUndo: qs('[data-rps-draw-undo]'),
    drawClear: qs('[data-rps-draw-clear]'),
    drawReadout: qs('[data-rps-draw-readout]'),
    drawError: qs('[data-rps-draw-error]'),
    drawErrorText: qs('[data-rps-draw-error-text]'),
    drawRetry: qs('[data-rps-draw-retry]'),
    bar: qs('[data-rps-bar]'),
    drawUse: qs('[data-rps-draw-use]'),
    stylesNote: qs('[data-rps-styles-note]'),
    colourRow: qs('[data-rps-colour-row]'),
    colourInput: qs('[data-rps-colour]'),
    colourValue: qs('[data-rps-colour-value]'),
    colourReset: qs('[data-rps-colour-reset]'),
    previewImg: qs('[data-rps-preview-img]'),
    posterEmpty: qs('[data-rps-poster-empty]'),
    posterExample: qs('[data-rps-poster-example]'),
    posterLoading: qs('[data-rps-poster-loading]'),
    posterLoadingNote: qs('[data-rps-poster-loading-note]'),
    previewStale: qs('[data-rps-preview-stale]'),
    previewStage: qs('[data-rps-stage]'),
    previewCol: qs('.rps__preview'),
    previewError: qs('[data-rps-preview-error]'),
    previewErrorText: qs('[data-rps-preview-error-text]'),
    previewRetry: qs('[data-rps-preview-retry]'),
    poster: qs('[data-rps-poster]'),
    zoom: qs('[data-rps-zoom]'),
    zoomImg: qs('[data-rps-zoom-img]'),
    overlay: qs('[data-rps-overlay]'),
    overlayText: qs('[data-rps-overlay-text]'),
    sumSize: qs('[data-rps-sum-size]'),
    sumPresentation: qs('[data-rps-sum-presentation]'),
    sumStyle: qs('[data-rps-sum-style]'),
    sumDigital: qs('[data-rps-sum-digital]'),
    sumTotals: qsa('[data-rps-sum-total]'),
    launchPill: qs('[data-rps-launch-pill]'),
    reviewList: qs('[data-rps-review-list]'),
    primaries: qsa('[data-rps-primary]'),
    primaryLabels: qsa('[data-rps-primary-label]'),
    backGlobal: qs('[data-rps-back-global]'),
    steps: qsa('[data-rps-step]'),
    giftToggle: qs('[data-rps-gift-toggle]'),
    giftFields: qs('[data-rps-gift-fields]'),
    giftMessage: qs('[data-rps-gift-message]'),
    ctaHints: qsa('[data-rps-cta-hint]'),
    barError: qs('[data-rps-bar-error]'),
    addonPrice: qs('[data-rps-addon-price]')
  };
  STEP_ORDER.forEach(function (s) {
    el.panels[s] = qs('[data-rps-panel="' + s + '"]');
  });

  // ---- backend (through the signed app proxy) ----------------------------
  function proxyFetch(path, opts) {
    opts = opts || {};
    var init = { method: opts.method || 'GET', headers: opts.headers || {} };
    if (opts.json !== undefined) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(opts.json); }
    else if (opts.body !== undefined) { init.body = opts.body; }
    // Every request gets a deadline. Without one, a request that never settles (flaky
    // mobile network, dropped connection) leaves its flow waiting forever; a tester lost
    // a whole draw session to exactly that (2026-07-02: requests stopped arriving for
    // ~3 minutes and the UI had nothing to time out on). Callers doing heavier work
    // (file upload, imports, preview render) pass a larger timeoutMs.
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    if (ctrl) {
      init.signal = ctrl.signal;
      timer = setTimeout(function () { ctrl.abort(); }, opts.timeoutMs || 20000);
    }
    var clear = function () { if (timer) { clearTimeout(timer); timer = null; } };
    return fetch(PROXY + path, init).then(function (res) {
      return res.text().then(function (text) {
        clear();
        var data = {};
        try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
        if (!res.ok) {
          var msg = (data && data.error && data.error.message) || 'Something went wrong. Please try again.';
          var err = new Error(msg); err.code = data && data.error && data.error.code; throw err;
        }
        return data;
      });
    }).catch(function (e) {
      clear();
      if (e && e.name === 'AbortError') {
        var t = new Error('The connection timed out. Check your signal and try again.');
        t.code = 'timeout';
        throw t;
      }
      throw e;
    });
  }

  function ensureJob() {
    if (state.jobId) return Promise.resolve(state.jobId);
    if (state.jobPromise) return state.jobPromise;
    state.jobPromise = proxyFetch('/jobs', { method: 'POST' }).then(function (data) {
      setJobId(data.publicId);
      return state.jobId;
    }).catch(function (e) { state.jobPromise = null; throw e; });
    return state.jobPromise;
  }

  // ---- session persistence (survive an accidental refresh) ----------------
  // The working state lives only in memory, so a full page load (an accidental mobile
  // pull-to-refresh on this tall page, an OS tab reclaim) minted a brand-new job and
  // orphaned the customer's imported route + design. Persist the job id so init() can
  // re-adopt a still-editable job from the server (the authoritative design store) and
  // resume rather than reset. Only the id is stored; no route data touches the browser.
  var SESSION_KEY = 'rps-session';
  function persistSession() { try { if (state.jobId) sessionStorage.setItem(SESSION_KEY, JSON.stringify({ jobId: state.jobId })); } catch (e) { /* private mode: acceptable */ } }
  function clearSession() { try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* noop */ } }
  function setJobId(id) { state.jobId = id; persistSession(); }

  // On load, re-adopt a still-in-progress job so a refresh resumes the design.
  function tryRestoreSession() {
    var jobId = null;
    try { var raw = sessionStorage.getItem(SESSION_KEY); if (raw) { var s = JSON.parse(raw); jobId = s && s.jobId; } } catch (e) { return; }
    if (!jobId || state.jobId) return; // nothing saved, or a job already adopted this load
    // A Strava OAuth return owns the session (handleStravaReturn); never fight it.
    try { if (new URLSearchParams(window.location.search).get('rps_strava')) return; } catch (e) {}
    proxyFetch('/jobs/' + jobId).then(function (st) {
      if (state.jobId) return; // a concurrent action adopted a job meanwhile
      var editable = st && ['draft', 'route_imported', 'preview_ready'].indexOf(st.status) !== -1;
      if (!editable || !st.hasRoute || !st.design) { clearSession(); return; } // locked / ordered / gone: start fresh
      restoreFromServer(jobId, st);
    }).catch(function () { /* stale or removed job: silently start fresh */ });
  }

  // Re-hydrate the studio from a server job state (the design is authoritative on the
  // server, saved on every edit). Uses the same setters as normal selection so the ARIA
  // radiogroups / previews stay consistent, then lands the customer on Review.
  function restoreFromServer(jobId, st) {
    setJobId(jobId);
    var d = st.design || {};
    if (d.styleId && !LOCKED.has(d.styleId)) selectStyle(d.styleId);
    if (d.routeColour) { state.design.routeColour = d.routeColour; syncColourControl(); }
    var p = d.product || {};
    if (p.size) selectSize(p.size);
    if (p.presentation) selectPresentation(p.presentation);
    var dig = qs('[data-rps-digital]');
    if (dig && !dig.disabled) { dig.checked = !!p.digitalCopy; state.design.product.digitalCopy = !!p.digitalCopy; }
    if (d.text) {
      state.design.text = Object.assign({ distanceUnit: 'miles' }, d.text);
      ['title', 'subtitle', 'date', 'distance', 'elevation', 'finishTime', 'averagePace'].forEach(function (k) {
        setInput(k, d.text[k] || '');
        if (d.text[k]) state.edited[k] = true; // restored text is user-owned; a later refill must not clobber it
      });
      setUnitUI(state.design.text.distanceUnit || 'miles');
    }
    state.hasRoute = true;
    state.source = st.sourceType || null;
    state.routeMetres = typeof st.distanceMetres === 'number' ? st.distanceMetres : null;
    state.routeMovingSeconds = typeof st.movingSeconds === 'number' ? st.movingSeconds : null;
    state.routeElevationGainMetres = typeof st.elevationGainMetres === 'number' ? st.elevationGainMetres : null;
    if (el.stravaCredit) el.stravaCredit.hidden = state.source !== 'strava_activity';
    if (el.routeSummary) { show(el.routeSummary); if (el.routeSummaryText) el.routeSummaryText.textContent = routeSummaryText(st); }
    updateSummary();
    updateGate();
    setStep('review', { scroll: false });
    renderPreview();
  }

  // ---- GPX upload --------------------------------------------------------
  function onGpxSelected(file) {
    if (state.applyingRoute) return; // a route import is already in flight; ignore a second concurrent source
    hide(el.routeError); hide(el.routeNote);
    if (!file) return;
    if (!/\.gpx$/i.test(file.name)) { return showRouteError('That is not a GPX file. Please choose a file ending in .gpx.'); }
    if (file.size > MAX_UPLOAD) { return showRouteError('That file is too large. GPX files are usually well under 10 MB.'); }

    setGpxStatus('Reading "' + file.name + '"…', false);
    startApplyingRoute();
    ensureJob()
      .then(function (id) {
        return proxyFetch('/jobs/' + id + '/upload-gpx?filename=' + encodeURIComponent(file.name), {
          method: 'POST', headers: { 'Content-Type': 'application/gpx+xml' }, body: file, timeoutMs: 60000
        });
      })
      .then(function (st) {
        // The route-summary below is the single confirmation; clear the upload status
        // so we do not duplicate "imported" (operator request).
        setGpxStatus('', false);
        onRouteImported(st);
      })
      .catch(function (e) { markPreviewError(new Error(e.message || 'We could not use that file.')); showRouteError(e.message); setGpxStatus('No file chosen yet.', false); });
  }

  // Shared route-imported path for both GPX upload and Strava import.
  function onRouteImported(st) {
    state.hasRoute = !!st.hasRoute;
    state.source = st.sourceType || null;
    // Powered by Strava credit: only when the route actually came from Strava.
    if (el.stravaCredit) el.stravaCredit.hidden = state.source !== 'strava_activity';
    state.routeMetres = typeof st.distanceMetres === 'number' ? st.distanceMetres : null;
    state.routeMovingSeconds = typeof st.movingSeconds === 'number' ? st.movingSeconds : null;
    state.routeElevationGainMetres = typeof st.elevationGainMetres === 'number' ? st.elevationGainMetres : null;
    // A route imported from Strava must show in the exact units the athlete uses there
    // (km/miles, /km-/mi pace, m/ft elevation). Set the units BEFORE the summary + fields
    // are formatted. Only Strava carries a user unit preference; GPX/draw keep the default.
    if (state.source === 'strava_activity' && (st.unitSystem === 'imperial' || st.unitSystem === 'metric')) {
      applyStravaUnits(st.unitSystem);
    }
    hide(el.routeError); hide(el.routeNote);
    show(el.routeSummary);
    el.routeSummaryText.textContent = routeSummaryText(st);
    applyRouteDefaults(st);
    updateStravaUnitHint(st); // fail-soft: warn if a Strava import couldn't confirm the athlete's units
    updateGate(); // re-enables the primary action ("Next: choose style") now that a route exists
    // NB the preview was already cleared + the in-flight render invalidated on CLICK
    // (startApplyingRoute -> resetPreviewForNewRoute), so a FAILED switch can never leave
    // the previous route's image on screen when the overlay lifts.
    scheduleSave(true); // first save + preview so the artwork starts generating as soon as a route is added
    // First import: move the flow forward to Style automatically (momentum) and land at the
    // TOP of "Choose your style" so the style options are in view (the route picker could
    // have left the page scrolled well down). A re-import deeper in the flow instead brings
    // the customer to the preview so they watch the print update.
    if (state.currentStep === 'route') {
      setStep('style', { scroll: false });
      scrollToStylePanel();
    } else {
      scrollToPreview();
    }
  }

  // A new route was just selected. Wipe the on-screen preview back to the loading
  // state and bump the preview token so a render still in flight for the PREVIOUS
  // route is discarded when it arrives. Without this, switching routes could leave
  // the earlier route's image on screen (operator-reported). The fresh render that
  // scheduleSave(true) kicks off then fills the cleared stage with the new route.
  function resetPreviewForNewRoute() {
    state.previewToken++;          // any earlier render's onload now sees a stale token and bails
    state.previewRendered = false;
    state.previewFailed = false;
    state.previewAttempt = 0;      // fresh route: reset the auto-retry budget
    saveGen++;                     // orphan any in-flight save chain (it must not render or release the lock)
    state.saving = false;          // release the serialise lock so the new render is never blocked
    state.pendingSave = false;
    hidePreviewError();
    markFresh();                   // drop the "preview is out of date" badge
    if (el.previewImg) { el.previewImg.hidden = true; el.previewImg.removeAttribute('src'); }
    setZoomable(false);            // no real print on screen yet: withdraw the zoom affordance
    hide(el.posterExample);        // the example print has done its job; the real preview takes over
    hide(el.posterEmpty);
    show(el.posterLoading);        // spinner until the new route's preview lands
  }

  function routeSummaryText(st) {
    var bits = [];
    if (st.routeName) bits.push(st.routeName);
    if (typeof st.distanceMetres === 'number') bits.push(formatDistanceWithUnit(st.distanceMetres, state.design.text.distanceUnit));
    return bits.length ? 'Imported: ' + bits.join(' · ') : 'Route imported.';
  }

  // Fill the print text from the imported activity. Route-derived fields REFRESH to
  // the newly selected activity on EVERY import (so switching activities updates the
  // title/stats, not just the map), but a field the customer has manually typed is
  // left alone. A field the new activity lacks is cleared (so stale values from the
  // previous activity never linger).
  function applyRouteDefaults(st) {
    var t = state.design.text;
    var moving = typeof st.movingSeconds === 'number' ? st.movingSeconds : 0;
    var hasDist = typeof st.distanceMetres === 'number';
    setRouteField('title', st.routeName || '');
    setRouteField('subtitle', st.subtitle || '');
    setRouteField('distance', hasDist ? formatDistanceWithUnit(st.distanceMetres, t.distanceUnit) : '');
    setRouteField('elevation', formatElevation(st.elevationGainMetres, t.distanceUnit));
    setRouteField('date', st.activityDate ? formatDate(st.activityDate) : '');
    setRouteField('finishTime', moving > 0 ? formatDuration(moving) : '');
    setRouteField('averagePace', (moving > 0 && hasDist && st.distanceMetres > 0) ? formatPace(moving, st.distanceMetres, t.distanceUnit) : '');
  }

  // Set a route-derived text field from an import, unless the customer edited it.
  function setRouteField(key, value) {
    if (state.edited[key]) return; // respect a manual edit across activity switches
    // Imported values bypass the inputs' maxlength (programmatic assignment): cap them
    // to the same limits so a 300-character activity name never flows into the design.
    var input = qs('[data-rps-text="' + key + '"]');
    var max = input && input.maxLength > 0 ? input.maxLength : 120;
    if (typeof value === 'string' && value.length > max) value = value.slice(0, max);
    state.design.text[key] = value;
    setInput(key, value);
  }

  function formatDistance(metres, unit) {
    var v = unit === 'kilometres' ? metres / 1000 : metres / 1609.344;
    // Match Strava's own display: two decimals, rounded DOWN (Strava's "tax"), e.g. a
    // 10.089 km run shows as 10.08. The +1e-6 guards the classic float-floor bug
    // (10.08 * 100 = 1007.9999... which would otherwise floor to 10.07).
    return (Math.floor(v * 100 + 1e-6) / 100).toString();
  }
  // The distance abbreviation for the active unit ("km" for metric, "mi" for imperial).
  function distanceUnitLabel(unit) { return unit === 'kilometres' ? 'km' : 'mi'; }
  // Distance number WITH its unit suffix, e.g. "10.08 km" / "6.21 mi". This is what
  // fills the editable Distance field (and therefore the printed poster), so the number
  // always reads with the unit the athlete used on Strava, just like elevation ("m"/"ft")
  // and pace ("/km"/"/mi") already do. Empty string when there is no distance.
  function formatDistanceWithUnit(metres, unit) {
    if (typeof metres !== 'number' || !isFinite(metres)) return '';
    return formatDistance(metres, unit) + ' ' + distanceUnitLabel(unit);
  }
  // Elevation gain in the unit system's own height unit: metres (metric) or feet
  // (imperial), matching how Strava shows it. Empty string when there is no value.
  function formatElevation(metres, unit) {
    if (typeof metres !== 'number' || !isFinite(metres)) return '';
    return unit === 'kilometres' ? Math.round(metres) + ' m' : Math.round(metres * 3.280839895) + ' ft';
  }
  function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch (e) { return ''; }
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function formatDuration(totalSec) {
    var s = Math.round(totalSec); if (!(s > 0)) return '';
    var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? h + ':' + pad2(m) + ':' + pad2(sec) : m + ':' + pad2(sec);
  }
  function formatPace(movingSec, metres, unit) {
    if (!movingSec || !metres) return '';
    var unitM = unit === 'kilometres' ? 1000 : 1609.344;
    var secPerUnit = movingSec / (metres / unitM);
    var m = Math.floor(secPerUnit / 60), sec = Math.round(secPerUnit % 60);
    if (sec === 60) { m += 1; sec = 0; }
    return m + ':' + pad2(sec) + ' /' + (unit === 'kilometres' ? 'km' : 'mi');
  }

  // The photo-upload UI for the parked "Medal Relief Photo" style has been removed (the
  // style card + photo markup are not in the template). To bring that style back, restore
  // this block from git history and re-add the style card + photo panel to the section.

  // ---- Strava connect + activity picker ----------------------------------
  function connectStrava() {
    if (!STRAVA_ENABLED || !STRAVA_START) {
      return showRouteNote('Strava import is not available yet. For now, please upload a GPX file below.');
    }
    showOverlay('Connecting to Strava…');
    // The OAuth round-trip is a full navigation: stash the design so a style chosen
    // from the gallery (or any typed text) survives the trip instead of silently
    // reverting to defaults on return.
    try { sessionStorage.setItem('rps-design-stash', JSON.stringify({ design: state.design, edited: state.edited })); } catch (e) { /* private mode: acceptable loss */ }
    ensureJob()
      .then(function (id) {
        var sep = STRAVA_START.indexOf('?') >= 0 ? '&' : '?';
        window.location.href = STRAVA_START + sep + 'jobId=' + encodeURIComponent(id);
      })
      .catch(function (e) { hideOverlay(); showRouteError(e.message || 'Could not start the Strava connection. Please try again.'); });
  }

  // The source tiles stay visible so the customer can switch source at any time. Opening
  // a picker shows that one and hides the other two, so only one is ever open at once.
  function openStravaPicker() {
    hide(el.popularPanel); hide(el.drawPanel);
    hide(el.routeError); hide(el.routeNote);
    show(el.stravaPanel);
    focusPanel(el.stravaPanel);
  }

  // ---- Popular-route picker (ready-made library) -------------------------
  function openPopularPicker() {
    if (!el.popularPanel) return;
    hide(el.stravaPanel); hide(el.drawPanel);
    hide(el.routeError); hide(el.routeNote);
    show(el.popularPanel);
    scrollToPopularPicker(); // scroll to the picker the moment it appears, not after the fetch resolves (operator request)
    focusPanel(el.popularPanel); syncSourceExpanded();
    loadPopularRoutes();
  }
  function loadPopularRoutes() {
    if (state.popularLoaded) { applyPopularView(); return; } // cached: fetch once
    el.popularList.setAttribute('aria-busy', 'true');
    proxyFetch('/popular-routes')
      .then(function (data) { state.popularLoaded = (data && data.routes) || []; applyPopularView(); })
      .catch(function (e) {
        el.popularList.setAttribute('aria-busy', 'false');
        el.popularList.innerHTML = '';
        showRouteNote(e.message || 'We could not load the routes. Please upload a GPX file instead.');
      });
  }
  function applyPopularView() {
    if (!el.popularList) return;
    el.popularList.setAttribute('aria-busy', 'false');
    var q = (state.popularSearch || '').trim().toLowerCase();
    var view = (state.popularLoaded || []).filter(function (r) {
      if (!q) return true;
      return (String(r.name) + ' ' + String(r.place)).toLowerCase().indexOf(q) >= 0;
    });
    if (el.popularStatus) el.popularStatus.textContent = view.length ? (view.length + (view.length === 1 ? ' route' : ' routes')) : 'No routes match your search.';
    el.popularList.innerHTML = '';
    if (!view.length) { if (el.popularNoResults) show(el.popularNoResults); return; }
    if (el.popularNoResults) hide(el.popularNoResults);
    view.forEach(function (r) {
      var isSel = r.id === state.selectedPopularId;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rps__activity' + (isSel ? ' is-selected' : '');
      btn.dataset.popularId = r.id;
      if (isSel) btn.setAttribute('aria-current', 'true');
      var meta = [r.place];
      if (typeof r.distanceKm === 'number') meta.push(r.distanceKm + ' km');
      if (r.sportType) meta.push(r.sportType);
      btn.innerHTML =
        '<span class="rps__activity-main">' +
          '<span class="rps__activity-name">' + esc(r.name) + '</span>' +
          '<span class="rps__activity-meta">' + esc(meta.join(' · ')) + '</span>' +
          '<span class="rps__activity-included">' + esc(r.descriptor || 'Ready-made route included') + '</span>' +
        '</span>' + activityTrailing(true, isSel);
      btn.addEventListener('click', function () { importPopular(r.id, btn); });
      el.popularList.appendChild(btn);
    });
  }
  function importPopular(routeId, btn) {
    if (btn) btn.classList.add('is-busy');
    qsa('.rps__activity', el.popularList).forEach(function (b) { b.disabled = true; });
    startApplyingRoute();
    ensureJob()
      .then(function (id) { return proxyFetch('/jobs/' + id + '/import-popular-route', { method: 'POST', json: { routeId: routeId }, timeoutMs: 35000 }); })
      .then(function (st) {
        // Keep the picker open with this route marked selected so the customer can switch easily.
        state.selectedPopularId = routeId;
        setGpxStatus('Route added.', true);
        applyPopularView();
        onRouteImported(st);
      })
      .catch(function (e) {
        if (btn) btn.classList.remove('is-busy');
        qsa('.rps__activity', el.popularList).forEach(function (b) { b.disabled = false; });
        markPreviewError(new Error(e.message || 'We could not switch to that route. Please try another.')); // show it at the preview + drop the overlay
        showRouteNote(e.message || 'We could not add that route. Please try another.');
      });
  }

  // ---- Draw your own route -----------------------------------------------
  // An interactive Leaflet map (vendored, lazy-loaded). The customer taps to drop
  // ordered points; we debounce-call the backend, which SNAPS them to roads (spline
  // fallback) and returns the line for the map to show. "Use this route" commits it
  // through the same onRouteImported path as GPX/popular/Strava. Raw coordinates that
  // come back are the customer's OWN drawn route, shown back to their own session.
  var leafletPromise = null;
  function ensureLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    var jsUrl = root.dataset.leafletJs;
    var cssUrl = root.dataset.leafletCss;
    if (!jsUrl) return Promise.reject(new Error('Map library is unavailable.'));
    leafletPromise = new Promise(function (resolve, reject) {
      if (cssUrl && !document.querySelector('link[data-rps-leaflet]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet'; link.href = cssUrl; link.setAttribute('data-rps-leaflet', '');
        document.head.appendChild(link);
      }
      var s = document.createElement('script');
      s.src = jsUrl; s.async = true;
      // Reset the memoised promise on failure so re-opening the picker retries the load.
      s.onload = function () { if (window.L) { resolve(window.L); } else { leafletPromise = null; reject(new Error('Map library failed to load.')); } };
      s.onerror = function () { leafletPromise = null; reject(new Error('Map library failed to load.')); };
      document.head.appendChild(s);
    });
    return leafletPromise;
  }

  function openDrawPicker() {
    if (!el.drawPanel) return;
    hide(el.stravaPanel); hide(el.popularPanel);
    hide(el.routeError); hide(el.routeNote);
    show(el.drawPanel);
    scrollToDrawPicker();
    focusPanel(el.drawPanel); syncSourceExpanded();
    ensureJob().catch(function () {}); // warm a job id so the first commit is quick
    ensureLeaflet()
      .then(function () { initDrawMap(); })
      .catch(function () { showDrawMapFail(); });
  }
  // Map is unusable (script failed to load): show the fallback message and hide the
  // now-dead controls so nothing invites a click that cannot work. The GPX uploader
  // and the (still-visible) source tiles remain the way forward.
  function showDrawMapFail() {
    if (el.drawMapFail) show(el.drawMapFail);
    if (el.drawTools) hide(el.drawTools);
    if (el.drawReadout) hide(el.drawReadout);
    if (el.drawUse) hide(el.drawUse);
  }

  function initDrawMap() {
    var L = window.L;
    if (!L || !el.drawMapEl) return;
    if (state.drawMap) { setTimeout(function () { state.drawMap.invalidateSize(); }, 60); return; }
    // doubleClickZoom OFF: a click adds a waypoint, so a double-tap (a natural "zoom in"
    // gesture, especially on mobile) would BOTH zoom and drop two near-duplicate pins that
    // distort the route. Pinch-zoom and the +/- controls remain; removing a pin is a tap.
    var map = L.map(el.drawMapEl, { center: [54.0, -2.6], zoom: 6, zoomControl: true, attributionControl: true, doubleClickZoom: false });
    var tiles = L.tileLayer(STADIA_TILES, { maxZoom: 19, attribution: STADIA_ATTR, crossOrigin: true }).addTo(map);
    state.drawLayer = L.layerGroup().addTo(map);
    map.on('click', function (e) { addDrawPin(e.latlng); });
    map.on('locationfound', function () { hideDrawError(); updateDrawReadout(); });
    map.on('locationerror', function () { showDrawError('We could not find your location. Pan to your area, then tap to add points.'); updateDrawReadout(); });
    // If the basemap tiles cannot load (e.g. the Stadia domain whitelist is not set up
    // yet), say so once instead of leaving a blank grey map; the route line + snapping
    // still work, so this is a non-blocking hint, not the full failure overlay.
    tiles.on('tileerror', function () {
      state.drawTileErrors++;
      if (state.drawTileErrors === 8 && el.drawError && el.drawError.hidden) {
        showDrawError('The map could not load its tiles. You can still draw, or upload a GPX file instead.');
      }
    });
    state.drawMap = map;
    // The container was hidden until just now; Leaflet must re-measure it.
    setTimeout(function () { map.invalidateSize(); }, 60);
  }

  function pinIcon(num, idx, total) {
    var cls = 'rps-pin' + (idx === 0 ? ' rps-pin--first' : (idx === total - 1 ? ' rps-pin--last' : ''));
    return window.L.divIcon({
      className: cls,
      html: '<span class="rps-pin__hit"><span class="rps-pin__dot">' + num + '</span></span>',
      iconSize: [40, 40], iconAnchor: [20, 20]
    });
  }
  // Rebuild all markers from state.drawPins. Called on add/remove (never mid-drag),
  // so re-creating markers cannot interrupt a drag. Drag only mutates a pin's coords.
  function rebuildDrawMarkers() {
    var L = window.L; if (!L || !state.drawMap) return;
    state.drawMarkers.forEach(function (m) { state.drawLayer.removeLayer(m); });
    state.drawMarkers = [];
    var n = state.drawPins.length;
    state.drawPins.forEach(function (pin, i) {
      var m = L.marker([pin.lat, pin.lng], { draggable: true, keyboard: false, icon: pinIcon(i + 1, i, n) });
      m.on('dragend', function () { var ll = m.getLatLng(); pin.lat = ll.lat; pin.lng = ll.lng; scheduleDrawSnap(); });
      m.on('click', function (ev) { L.DomEvent.stopPropagation(ev); removeDrawPinAt(i); });
      m.addTo(state.drawLayer);
      state.drawMarkers.push(m);
    });
  }

  function addDrawPin(latlng) {
    if (!state.drawMap) return;
    if (state.drawPins.length >= MAX_DRAW_POINTS) { showDrawError('That is the most points we can use. Remove one to add another.'); return; }
    hideDrawError(); hide(el.drawNudge);
    state.drawPins.push({ lat: latlng.lat, lng: latlng.lng });
    rebuildDrawMarkers();
    updateDrawTools();
    scheduleDrawSnap();
  }
  function removeDrawPinAt(i) {
    if (i < 0 || i >= state.drawPins.length) return;
    state.drawPins.splice(i, 1);
    rebuildDrawMarkers();
    updateDrawTools();
    scheduleDrawSnap();
  }
  function undoDrawPin() { if (state.drawPins.length) removeDrawPinAt(state.drawPins.length - 1); }
  function clearDraw() {
    state.drawPins = [];
    state.drawPreviewReady = false;
    state.drawLastKey = '';
    state.drawLastDistance = null;
    state.drawSnapToken++; // discard any snap in flight
    rebuildDrawMarkers();
    drawRemoveLines();
    hideDrawError();
    if (el.drawNudge) show(el.drawNudge);
    updateDrawTools();
    updateDrawReadout();
    updateDrawUse();
  }
  // Add a point at the current map centre. Gives keyboard / switch users (and anyone
  // who finds precise tapping hard) a non-pointer way to place a waypoint: focus the
  // map, arrow-pan, then activate this button. Undo/Clear/Use are all buttons too.
  function addDrawPinAtCentre() { if (state.drawMap) addDrawPin(state.drawMap.getCenter()); }

  function drawRemoveLines() {
    if (state.drawRawLine) { state.drawLayer.removeLayer(state.drawRawLine); state.drawRawLine = null; }
    if (state.drawSnapLine) { state.drawLayer.removeLayer(state.drawSnapLine); state.drawSnapLine = null; }
  }
  // Instant straight-line feedback while the road-snap request is in flight.
  function drawRawFeedback() {
    if (!state.drawMap) return;
    if (state.drawRawLine) { state.drawLayer.removeLayer(state.drawRawLine); state.drawRawLine = null; }
    if (state.drawPins.length < 2) return;
    var latlngs = state.drawPins.map(function (p) { return [p.lat, p.lng]; });
    state.drawRawLine = window.L.polyline(latlngs, { color: '#6a655c', weight: 2, opacity: 0.5, dashArray: '4 5', interactive: false }).addTo(state.drawLayer);
  }
  function drawSnappedLine(line) {
    if (!state.drawMap || !Array.isArray(line)) return;
    drawRemoveLines();
    var latlngs = line.map(function (c) { return [c[1], c[0]]; }); // [lon,lat] -> [lat,lng]
    state.drawSnapLine = window.L.polyline(latlngs, { color: DRAW_LINE_COLOUR, weight: 4, opacity: 0.95, lineJoin: 'round', lineCap: 'round', interactive: false }).addTo(state.drawLayer);
  }

  // Fingerprint of the current pin set, so we never re-issue an identical (billable)
  // snap request, e.g. when a drag ends at the same spot.
  function drawKey() {
    return state.drawPins.map(function (p) { return p.lng.toFixed(6) + ',' + p.lat.toFixed(6); }).join(';');
  }
  function scheduleDrawSnap() {
    drawRawFeedback();
    if (state.drawSnapTimer) clearTimeout(state.drawSnapTimer);
    state.drawPreviewReady = false; // pins changed; the previous snapped line is now stale
    updateDrawUse();
    updateDrawReadout(); // drop the "now tap Use this route" prompt immediately (Use is now disabled)
    if (state.drawPins.length < 2) {
      drawRemoveLines();
      state.drawLastKey = '';
      state.drawLastDistance = null;
      updateDrawReadout();
      return;
    }
    var key = drawKey();
    if (key === state.drawLastKey && state.drawSnapLine) {
      // Identical to the last snapped set: reuse it, no network call.
      state.drawPreviewReady = true;
      updateDrawReadout();
      updateDrawUse();
      return;
    }
    state.drawSnapTimer = setTimeout(runDrawSnap, 450);
  }
  // Live PREVIEW snap: turns the current pins into a road-snapped line for the draw
  // map only. It never persists, so a half-drawn route can never become the committed
  // route; that happens only on "Use this route" (useDrawnRoute -> the commit endpoint).
  function runDrawSnap() {
    if (state.drawPins.length < 2) return;
    var pins = state.drawPins.slice();
    var key = drawKey();
    var token = ++state.drawSnapToken;
    setDrawReadout('Tracing the roads…', false);
    hideDrawError();
    ensureJob()
      .then(function (id) {
        var points = pins.map(function (p) { return [p.lng, p.lat]; }); // [lon,lat]
        return proxyFetch('/jobs/' + id + '/draw-route', { method: 'POST', json: { points: points } });
      })
      .then(function (data) {
        if (token !== state.drawSnapToken) return; // a newer edit superseded this snap
        if (data.line) drawSnappedLine(data.line);
        state.drawPreviewReady = true;
        state.drawLastKey = key;
        state.drawLastDistance = typeof data.distanceMetres === 'number' ? data.distanceMetres : null;
        updateDrawReadout();
        updateDrawUse();
      })
      .catch(function (e) {
        if (token !== state.drawSnapToken) return;
        // A DETERMINISTIC validation failure (too large / too many / bad points) re-fails
        // identically on retry, so hide the Try-again button and let the message guide the
        // user to change the points instead. Connectivity + unknown failures stay retryable.
        var connectivity = e && (e.code === 'timeout' || e.name === 'TypeError');
        var VALIDATION = ['route_too_large', 'invalid_points', 'too_many_points', 'too_few_points', 'no_route'];
        var validation = e && e.code && VALIDATION.indexOf(e.code) !== -1;
        var msg = connectivity
          ? 'We could not trace your route just now. Check your signal, then tap Try again.'
          : (e && e.message) || 'We could not draw that route. Move a point and try again.';
        showDrawError(msg, !validation);
        updateDrawReadout(); // back to the point-count readout
      });
  }

  // Commit the drawn route: persist it on the job (the only draw path that writes),
  // then flow into the shared onRouteImported path (preview + advance). startApplyingRoute
  // clears the old preview + invalidates any in-flight render, matching the other sources.
  function useDrawnRoute() {
    if (!state.drawPreviewReady || state.drawPins.length < 2 || state.drawCommitting) return;
    var pins = state.drawPins.slice();
    state.drawCommitting = true;
    updateDrawUse();
    startApplyingRoute();
    ensureJob()
      .then(function (id) {
        var points = pins.map(function (p) { return [p.lng, p.lat]; });
        return proxyFetch('/jobs/' + id + '/draw-route/commit', { method: 'POST', json: { points: points }, timeoutMs: 35000 });
      })
      .then(function (st) {
        state.drawCommitting = false;
        setGpxStatus('Route added.', true);
        onRouteImported(st);
        updateDrawUse();
      })
      .catch(function (e) {
        state.drawCommitting = false;
        markPreviewError(new Error((e && e.message) || 'We could not use that route. Please try again.'));
        updateDrawUse();
      });
  }
  function drawLocate() {
    if (!state.drawMap) return;
    hideDrawError();
    if (!navigator.geolocation) { showDrawError('Location is not available here. Pan to your area, then tap to add points.'); return; }
    setDrawReadout('Finding your location…', false);
    state.drawMap.locate({ setView: true, maxZoom: 15, enableHighAccuracy: true });
  }

  function updateDrawTools() {
    var n = state.drawPins.length;
    if (el.drawUndo) el.drawUndo.disabled = n < 1;
    if (el.drawClear) el.drawClear.disabled = n < 1;
  }
  function updateDrawUse() {
    if (!el.drawUse) return;
    var ready = state.drawPreviewReady && state.drawPins.length >= 2 && !state.drawCommitting;
    el.drawUse.disabled = !ready;
    // "is-ready" fires a one-time brass-ring nudge so the commit step is obvious; a drawn
    // route is not accepted (and "Next: choose style" cannot appear) until this is clicked.
    el.drawUse.classList.toggle('is-ready', ready);
  }
  function drawDistanceLabel(metres) {
    return formatDistanceWithUnit(metres, state.design.text.distanceUnit);
  }
  function updateDrawReadout() {
    var n = state.drawPins.length;
    if (n === 0) { setDrawReadout('Tap the map to start your route', false); return; }
    if (n === 1) { setDrawReadout('Add one more point', false); return; }
    var dist = (typeof state.drawLastDistance === 'number') ? drawDistanceLabel(state.drawLastDistance) : '';
    var base = n + ' points' + (dist ? ' · ' + dist : '');
    // Once the road-snapped line is ready, tell them the one remaining action explicitly.
    if (state.drawPreviewReady) base += ' · now tap "Use this route" to continue';
    setDrawReadout(base, true);
  }
  function setDrawReadout(text, active) { if (el.drawReadout) { el.drawReadout.textContent = text; el.drawReadout.classList.toggle('is-active', !!active); } }
  function showDrawError(msg, retryable) {
    if (el.drawErrorText) el.drawErrorText.textContent = msg;
    else if (el.drawError) el.drawError.textContent = msg;
    if (el.drawRetry) el.drawRetry.hidden = !retryable;
    if (el.drawError) show(el.drawError);
  }
  function hideDrawError() { if (el.drawError) hide(el.drawError); }

  function loadActivities() {
    if (!state.jobId) return Promise.resolve();
    if (el.stravaList) el.stravaList.setAttribute('aria-busy', 'true');
    hide(el.stravaEmpty); hide(el.stravaReconnect);
    el.stravaList.innerHTML = '<div class="rps__strava-loading"><span class="rps__spinner" aria-hidden="true"></span><span>Loading your activities…</span></div>';
    return proxyFetch('/jobs/' + state.jobId + '/strava-activities')
      .then(function (data) {
        // Adopt the athlete's Strava units as soon as the picker loads, so the previews
        // (and then the imported print) are all in km/miles to match Strava.
        if (data && (data.unitSystem === 'imperial' || data.unitSystem === 'metric')) {
          var unit = data.unitSystem === 'imperial' ? 'miles' : 'kilometres';
          if (state.design.text.distanceUnit !== unit) { state.design.text.distanceUnit = unit; setUnitUI(unit); rederiveUnitFields(); }
        }
        renderActivities((data && data.activities) || []);
      })
      .catch(function (e) {
        if (el.stravaList) { el.stravaList.setAttribute('aria-busy', 'false'); el.stravaList.innerHTML = ''; }
        el.stravaHint.textContent = (e.code === 'strava_expired' || e.code === 'not_connected')
          ? 'Your Strava session has ended. Reconnect to choose an activity.'
          : (e.message || 'We could not load your Strava activities.');
        show(el.stravaReconnect);
      });
  }

  // Bucket a Strava sport_type into the picker's filter chips.
  function activityCategory(sportType) {
    var s = String(sportType || '').toLowerCase();
    if (s.indexOf('run') >= 0) return 'run';
    if (s === 'walk' || s === 'hike') return 'walk';
    if (s.indexOf('ride') >= 0 || s.indexOf('cycl') >= 0 || s.indexOf('bike') >= 0) return 'ride';
    return 'other';
  }
  function paceSecPerKm(a) {
    if (typeof a.distanceMetres !== 'number' || a.distanceMetres <= 0) return Infinity;
    if (typeof a.movingSeconds !== 'number' || a.movingSeconds <= 0) return Infinity;
    return a.movingSeconds / (a.distanceMetres / 1000);
  }
  // Tag the standout efforts within the TYPE the customer is browsing: the longest
  // distance and the fastest pace. Computed against the type-filtered set (all runs in
  // the Runs view, all walks in the Walks view) but DELIBERATELY ignoring the text
  // search, so searching for one activity can never make it "Longest"/"Fastest" by
  // default. Honest relative highlights, not all-time PBs. Pace is finite only with
  // distance + time. A comparison needs >= 2 usable activities, else there is no standout.
  function computeBadges(list) {
    var usable = list.filter(function (a) { return a.hasGps; });
    if (usable.length < 2) return { longestId: null, fastestId: null };
    var longestId = null, longestD = -1, fastestId = null, fastestP = Infinity;
    usable.forEach(function (a) {
      if (typeof a.distanceMetres === 'number' && a.distanceMetres > longestD) { longestD = a.distanceMetres; longestId = a.id; }
      var p = paceSecPerKm(a);
      if (p < fastestP) { fastestP = p; fastestId = a.id; }
    });
    return { longestId: longestId, fastestId: fastestId };
  }

  function renderActivities(list) {
    if (el.stravaList) el.stravaList.setAttribute('aria-busy', 'false');
    state.stravaActivities = Array.isArray(list) ? list : [];
    var usable = state.stravaActivities.filter(function (a) { return a.hasGps; });
    if (!usable.length) {
      el.stravaList.innerHTML = '';
      if (el.stravaTools) hide(el.stravaTools);
      if (el.stravaNoResults) hide(el.stravaNoResults);
      show(el.stravaEmpty); show(el.stravaReconnect);
      return;
    }
    hide(el.stravaEmpty);
    if (el.stravaTools) show(el.stravaTools);
    applyActivityView();
  }

  // Filter (type chip + search) and sort the loaded activities, then render.
  function applyActivityView() {
    if (!el.stravaList) return;
    var q = (state.activitySearch || '').trim().toLowerCase();
    var filter = state.activityFilter || 'all';
    // Badges compare against the TYPE-filtered set only (the category being browsed),
    // so the search box narrows what is shown without redefining "Longest"/"Fastest".
    var typeFiltered = state.stravaActivities.filter(function (a) {
      return filter === 'all' || activityCategory(a.sportType) === filter;
    });
    state.activityBadges = computeBadges(typeFiltered);
    var view = typeFiltered.filter(function (a) {
      return !q || String(a.name || '').toLowerCase().indexOf(q) >= 0;
    });
    var sort = state.activitySort || 'recent';
    view.sort(function (a, b) {
      if (a.hasGps !== b.hasGps) return Number(b.hasGps) - Number(a.hasGps); // usable first
      if (sort === 'distance') return (b.distanceMetres || 0) - (a.distanceMetres || 0);
      if (sort === 'pace') return paceSecPerKm(a) - paceSecPerKm(b);
      return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
    });
    if (el.stravaStatus) el.stravaStatus.textContent = view.length ? (view.length + (view.length === 1 ? ' activity' : ' activities')) : 'No activities match your search.';
    renderActivityCards(view);
  }

  // Trailing element for a route card: No-GPS tag, Selected pill, or the chevron.
  function activityTrailing(hasGps, isSel) {
    if (!hasGps) return '<span class="rps__activity-tag">No GPS</span>';
    if (isSel) return '<span class="rps__activity-selected"><svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="m5 13 4 4L19 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Selected</span>';
    return '<span class="rps__activity-go" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
  }

  function renderActivityCards(view) {
    el.stravaList.innerHTML = '';
    if (!view.length) { if (el.stravaNoResults) show(el.stravaNoResults); return; }
    if (el.stravaNoResults) hide(el.stravaNoResults);
    var unit = state.design.text.distanceUnit;
    var badges = state.activityBadges || {};
    view.forEach(function (a) {
      var isSel = a.hasGps && a.id === state.selectedActivityId;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rps__activity' + (a.hasGps ? '' : ' is-disabled') + (isSel ? ' is-selected' : '');
      btn.dataset.activityId = a.id;
      if (!a.hasGps) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); }
      if (isSel) btn.setAttribute('aria-current', 'true');
      var meta = [];
      if (a.date) meta.push(formatDate(a.date));
      if (typeof a.distanceMetres === 'number') meta.push(formatDistanceWithUnit(a.distanceMetres, unit));
      if (typeof a.elevationGainMetres === 'number') meta.push(formatElevation(a.elevationGainMetres, unit));
      if (a.sportType) meta.push(a.sportType);
      var tags = '';
      if (a.hasGps && a.id === badges.longestId) tags += '<span class="rps__activity-badge">Longest</span>';
      if (a.hasGps && a.id === badges.fastestId) tags += '<span class="rps__activity-badge rps__activity-badge--fast">Fastest</span>';
      btn.innerHTML =
        '<span class="rps__activity-main">' +
          '<span class="rps__activity-namerow"><span class="rps__activity-name">' + esc(a.name) + '</span>' + tags + '</span>' +
          '<span class="rps__activity-meta">' + esc(meta.join(' · ')) + '</span>' +
        '</span>' + activityTrailing(a.hasGps, isSel);
      if (a.hasGps) btn.addEventListener('click', function () { importActivity(a.id, btn); });
      el.stravaList.appendChild(btn);
    });
  }

  function importActivity(activityId, btn) {
    if (btn) btn.classList.add('is-busy');
    qsa('.rps__activity', el.stravaList).forEach(function (b) { b.disabled = true; });
    el.stravaHint.textContent = 'Importing your route…';
    startApplyingRoute();
    proxyFetch('/jobs/' + state.jobId + '/import-strava-activity', { method: 'POST', json: { activityId: activityId }, timeoutMs: 35000 })
      .then(function (st) {
        // Keep the picker open with this activity marked selected so the customer can
        // switch easily; the re-render re-enables the buttons and applies the state.
        state.selectedActivityId = activityId;
        setGpxStatus('Strava activity imported.', true);
        el.stravaHint.textContent = 'Route selected. Choose another any time to switch.';
        applyActivityView();
        onRouteImported(st);
      })
      .catch(function (e) {
        if (btn) btn.classList.remove('is-busy');
        qsa('.rps__activity', el.stravaList).forEach(function (b) { if (!b.classList.contains('is-disabled')) b.disabled = false; });
        // Surface the failure AT THE PREVIEW (the customer is watching the print, not
        // the Strava panel), so a failed switch shows a clear reason instead of the
        // previous route's image lingering. markPreviewError also drops the overlay.
        var msg = (e.code === 'strava_expired' || e.code === 'not_connected')
          ? 'Your Strava session has ended, so the print could not switch to that activity. Reconnect Strava and choose it again.'
          : (e.message || 'We could not switch to that activity. Please try another.');
        markPreviewError(new Error(msg));
        if (e.code === 'strava_expired' || e.code === 'not_connected') {
          el.stravaHint.textContent = 'Your Strava session has ended. Reconnect to import.';
          show(el.stravaReconnect);
        } else {
          el.stravaHint.textContent = e.message || 'We could not import that activity. Please try another.';
        }
      });
  }

  function stravaErrorMessage(reason) {
    switch (reason) {
      case 'denied': return 'Strava connection was cancelled. You can try again or upload a GPX file.';
      case 'scope': return 'We need permission to read your activities. Please reconnect and allow access.';
      case 'state': return 'That Strava link has expired. Please connect again.';
      case 'unavailable': return 'Strava import is not available yet. Please upload a GPX file.';
      default: return 'We could not connect to Strava. Please try again or upload a GPX file.';
    }
  }

  // On returning from the Strava OAuth round-trip the backend redirects here with
  // a non-secret status param. Adopt the job, strip the params, show the picker.
  function handleStravaReturn() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var status = params.get('rps_strava');
    if (!status) return;
    // Restore the pre-OAuth design stash (style, colour, typed text + edited flags)
    // BEFORE the activity import runs, so applyRouteDefaults respects manual edits.
    // Product size/finish are deliberately NOT restored (their picker UI would desync).
    try {
      var rawStash = sessionStorage.getItem('rps-design-stash');
      if (rawStash) {
        sessionStorage.removeItem('rps-design-stash');
        var stash = JSON.parse(rawStash);
        if (stash && stash.design) {
          if (stash.design.styleId) selectStyle(stash.design.styleId);
          if (stash.design.routeColour) { state.design.routeColour = stash.design.routeColour; syncColourControl(); }
          if (stash.design.text) {
            state.design.text = stash.design.text;
            Object.keys(stash.design.text).forEach(function (k) { if (k !== 'distanceUnit') setInput(k, stash.design.text[k] || ''); });
            // setInput skips distanceUnit, so sync the Miles/Kilometres toggle to the
            // restored unit here; otherwise the toggle shows the default while state holds
            // the restored unit, and any manual distance/pace entry is read against the
            // wrong unit until an activity import happens to re-sync it.
            setUnitUI(state.design.text.distanceUnit || 'miles');
          }
          if (stash.edited) state.edited = stash.edited;
        }
      }
    } catch (e) { /* a lost stash only means default style/text, never an error */ }
    var jobId = params.get('jobId');
    var reason = params.get('reason');
    params.delete('rps_strava'); params.delete('jobId'); params.delete('reason');
    var rest = params.toString();
    try { window.history.replaceState({}, document.title, window.location.pathname + (rest ? '?' + rest : '') + window.location.hash); } catch (e) {}

    if (status === 'connected' && jobId) {
      setJobId(jobId);
      showOverlay('Loading your activities…');
      openStravaPicker();
      // When the activities have loaded, drop the overlay and bring the customer
      // down to the picker so they can see their activities have appeared.
      // Move focus into the picker AFTER hideOverlay() lifts the inert/aria-hidden shell:
      // openStravaPicker's focusPanel() ran while the overlay still had the shell inert, so
      // that focus() was a silent no-op and keyboard/SR focus was stranded on <body>.
      loadActivities().then(function () { hideOverlay(); scrollToStravaPicker(); focusPanel(el.stravaPanel); });
    } else if (status === 'error') {
      if (jobId) setJobId(jobId); // so a reconnect reuses the same job
      showRouteError(stravaErrorMessage(reason));
    }
  }

  // ---- design save + preview (serialised) --------------------------------
  var saveTimer = null;
  // withPreview=false persists the design (PATCH) but does NOT re-render the preview.
  // Used for the Size & finish step (size / presentation / digital), which never change
  // the artwork, so the preview must not flicker/refresh there.
  function scheduleSave(immediate, withPreview) {
    if (withPreview === undefined) withPreview = true;
    if (withPreview) {
      state.pendingPreview = true;
      // Show the loading veil over the preview the instant a preview-affecting change is
      // made (style, print text, units, route colour), so the customer sees right away
      // that a new preview is generating, before the debounce + render even begin. Only
      // when a route exists (otherwise the empty state stands and nothing will render).
      if (state.hasRoute) showPreviewWorking();
    }
    if (saveTimer) clearTimeout(saveTimer);
    if (immediate) { runSave(); return; }
    saveTimer = setTimeout(runSave, 500);
  }

  // Save generation: bumped on a route switch so a chain still in flight for the
  // PREVIOUS route can neither fire its completion render (a stale, billable
  // re-render of the just-cleared stage) nor release the serialisation lock that the
  // reset already force-released for the NEW route's chain.
  var saveGen = 0;
  function runSave() {
    if (!state.jobId || !state.hasRoute) return;
    if (state.saving) { state.pendingSave = true; return; } // serialise: no overlapping PATCHes
    state.saving = true;
    var gen = saveGen;
    var doPreview = state.pendingPreview;
    state.pendingPreview = false;
    var patch = proxyFetch('/jobs/' + state.jobId + '/design', { method: 'PATCH', json: state.design });
    // Track the PATCH alone (not the render tail) so add-to-cart can flush cheaply.
    state.patchInFlight = (state.patchInFlight || 0) + 1;
    patch.then(function () { state.patchInFlight -= 1; }, function () { state.patchInFlight -= 1; });
    patch
      .then(function () { if (gen !== saveGen) return null; return doPreview ? renderPreview() : null; })
      .catch(function (e) { if (gen === saveGen && doPreview) markPreviewError(e); })
      .then(function () {
        if (gen !== saveGen) return; // a route switch superseded this chain
        state.saving = false;
        if (state.pendingSave) { state.pendingSave = false; runSave(); }
      });
  }

  // Make sure the SERVER design matches what is on screen before locking for cart.
  // Without this, an edit still sitting in the 500ms debounce (or an in-flight PATCH
  // racing the lock) meant the customer could pay for the pre-edit design. Waits
  // briefly for any in-flight PATCH (bounded), then sends one final authoritative
  // PATCH of the current design.
  function flushDesignForCheckout() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    state.pendingSave = false;
    var waitInFlight = new Promise(function (resolve) {
      var waited = 0;
      var iv = setInterval(function () {
        waited += 100;
        if (!(state.patchInFlight > 0) || waited >= 3000) { clearInterval(iv); resolve(); }
      }, 100);
    });
    return waitInFlight.then(function () {
      return proxyFetch('/jobs/' + state.jobId + '/design', { method: 'PATCH', json: state.design });
    });
  }

  function renderPreview() {
    if (!state.jobId || !state.hasRoute) return Promise.resolve();
    var token = ++state.previewToken;
    hidePreviewError();
    hidePreviewNote();
    show(el.posterLoading);
    // A cold first render of a big coastal route (a large low-zoom map + the coastline
    // correction + any map-provider retry) legitimately takes a while AND then arrives,
    // so we must NOT cry failure while it is still on its way. Two-stage:
    //  - soft: after a short wait, add a calm "still working" note (no error, no button).
    //  - hard: only if it has neither loaded nor responded well beyond the server's own
    //    budget do we surface the Try-again control.
    var soft = setTimeout(function () {
      if (token === state.previewToken && !state.previewRendered) showPreviewNote();
    }, 8000);
    // The watchdog must sit ABOVE the fetch deadline (55s) so the fetch's own timeout
    // governs the server wait, and it must also cover the preview-image download/decode
    // that happens AFTER the fetch resolves. At 45s it undercut the 55s fetch, so a valid
    // but slow cold render (a big coastal route on a mobile link) was falsely failed and
    // the print left un-buyable. 65s = 55s server budget + headroom for a large JPEG over
    // a slow connection.
    var watchdog = setTimeout(function () {
      if (token !== state.previewToken) return;
      if (!state.previewRendered) failPreview(new Error('The preview is taking too long. Please try again.'));
    }, 65000);
    var clearTimers = function () { clearTimeout(soft); clearTimeout(watchdog); };
    // The preview render can legitimately take a while on a cold cache (the server
    // allows itself up to ~40s to fetch a basemap), so its deadline sits above that.
    return proxyFetch('/jobs/' + state.jobId + '/preview', { method: 'POST', timeoutMs: 55000 }).then(function (data) {
      if (token !== state.previewToken) { clearTimers(); return; }
      if (data && data.previewUrl) {
        var img = new Image();
        img.onload = function () {
          clearTimers();
          if (token !== state.previewToken) return;
          el.previewImg.src = data.previewUrl;
          el.previewImg.alt = previewAlt();
          var wasHidden = el.previewImg.hidden; // capture before we reveal it
          el.previewImg.hidden = false;
          // First paint + a new route reveal the image from nothing (rps-anim-in); an
          // in-place re-render (style / text, where the image is already shown) only dips
          // (rps-anim-refresh), so a single-character text edit never blanks the poster.
          el.previewImg.classList.remove('rps-anim-in', 'rps-anim-refresh');
          void el.previewImg.offsetWidth; // reflow so the chosen reveal animation restarts
          el.previewImg.classList.add(wasHidden ? 'rps-anim-in' : 'rps-anim-refresh');
          // The render succeeded: clear the loading veil, the "still working" note AND any
          // error a watchdog may have shown before this (slow) render finally arrived. This
          // is the fix for "the map appears but the timeout error stays on screen". The
          // route-level error is cleared too (the 70s apply watchdog may have cried failure
          // just before this slow render finally landed).
          hide(el.posterEmpty); hide(el.posterExample); hide(el.posterLoading); hidePreviewNote(); hidePreviewError();
          hide(el.routeError);
          state.previewRendered = true; state.previewFailed = false;
          state.previewAttempt = 0; // a good render clears the auto-retry budget
          setZoomable(true);        // a real print is now on screen: offer "Click to zoom"
          markFresh();
          updateGate();
          stopApplyingRoute(); // the new route's preview is on screen: drop the "Applying route…" overlay
        };
        img.onerror = function () {
          clearTimers();
          if (token === state.previewToken) failPreview(new Error('The preview image could not be loaded. Please try again.'));
        };
        img.src = data.previewUrl;
      } else {
        // 200 with no previewUrl is not success: route it through the same auto-retry +
        // surfaced-error path instead of silently leaving a spinner-less dead state.
        clearTimers();
        if (token === state.previewToken) failPreview(new Error('We could not generate your preview. Please try again.'));
      }
    }).catch(function (e) {
      clearTimers();
      if (token === state.previewToken) failPreview(e);
    });
  }
  function showPreviewNote() { if (el.posterLoadingNote) el.posterLoadingNote.hidden = false; }
  function hidePreviewNote() { if (el.posterLoadingNote) el.posterLoadingNote.hidden = true; }

  function previewAlt() {
    var d = state.design, style = STYLE_NAMES[d.styleId] || 'Pure';
    var title = (d.text.title || '').trim();
    return 'Preview of your ' + style + ' route print in size ' + d.product.size + (title ? ', titled "' + title + '"' : '') + '.';
  }
  // Show the loading veil over the preview while a new render is generating. Clears any
  // stale badge + error first so the spinner is the single, clear "working" signal.
  function showPreviewWorking() { hidePreviewError(); hidePreviewNote(); markFresh(); show(el.posterLoading); }
  function markFresh() { hide(el.previewStale); }
  // A preview render failed (timeout, image load, or the preview request). Auto-retry ONCE
  // silently, since the usual cause is a transient cold start / slow first map fetch that a
  // second attempt clears; only surface the error if it fails again in a row. (operator request)
  function failPreview(e) {
    state.previewAttempt = (state.previewAttempt || 0) + 1;
    if (state.previewAttempt < 2) { renderPreview(); return; }
    state.previewAttempt = 0;
    markPreviewError(e);
  }
  function markPreviewError(e) {
    stopApplyingRoute();
    hide(el.previewStale); hide(el.posterLoading); hidePreviewNote();
    state.previewFailed = true;
    if (el.previewErrorText) el.previewErrorText.textContent = (e && e.message) ? e.message : 'We could not update the preview.';
    show(el.previewError);
    // Re-derive the gate NOW: previewFailed must disable "Add to basket" immediately,
    // not leave an enabled button that silently no-ops on canAddToCart().
    updateGate();
  }
  function hidePreviewError() { hide(el.previewError); }

  // ---- selections (with roving tabindex) ---------------------------------
  function selectStyle(id) {
    if (LOCKED.has(id)) { show(el.stylesNote); return; }
    state.design.styleId = id;
    qsa('[data-rps-style]').forEach(function (b) {
      var on = b.dataset.rpsStyle === id;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    syncColourControl(); // when there is no custom colour, the swatch follows the new style
    updateSummary(); scheduleSave();
  }

  // ---- custom route line colour ------------------------------------------
  var HEX6 = /^#[0-9a-fA-F]{6}$/;
  // Reflect the current state into the picker: the swatch shows the custom colour
  // when set, otherwise the chosen style's own colour; the reset link + label only
  // appear once a custom colour is in effect.
  function syncColourControl() {
    if (!el.colourInput) return;
    var custom = HEX6.test(state.design.routeColour || '') ? state.design.routeColour.toLowerCase() : '';
    el.colourInput.value = custom || styleDefaultColour(state.design.styleId);
    if (el.colourReset) el.colourReset.hidden = !custom;
    if (el.colourValue) el.colourValue.textContent = custom ? custom.toUpperCase() : 'Style colour';
  }
  function selectRouteColour(value) {
    if (!HEX6.test(value || '')) return;
    state.design.routeColour = value.toLowerCase();
    syncColourControl();
    scheduleSave(); // a colour change DOES change the artwork: re-render the preview
  }
  function resetRouteColour() {
    if (!state.design.routeColour) { syncColourControl(); return; }
    state.design.routeColour = '';
    syncColourControl();
    scheduleSave();
  }

  function selectSize(size) {
    state.design.product.size = size;
    qsa('[data-rps-size]').forEach(function (b) {
      var on = b.dataset.rpsSize === size;
      b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
    });
    // Reflect the chosen size on the "On a wall" view so A1 visibly dominates and A4 reads
    // small (CSS scales the framed poster from this attribute). Artwork view is unaffected.
    if (el.previewStage) el.previewStage.dataset.rpsSize = size;
    updateSummary(); updateGate(); scheduleSave(false, false); // size never changes the artwork: no preview re-render
  }
  function selectPresentation(p) {
    state.design.product.presentation = p;
    qsa('[data-rps-presentation]').forEach(function (b) {
      var on = b.dataset.rpsPresentation === p;
      b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
    });
    el.previewStage.classList.toggle('is-framed', p === 'framed');
    updateSummary(); updateGate(); scheduleSave(false, false); // presentation is client-side only: no preview re-render
  }
  // Sync the Miles/Kilometres toggle's visual + ARIA state to the given unit.
  function setUnitUI(unit) {
    qsa('[data-rps-unit]').forEach(function (b) {
      var on = b.dataset.rpsUnit === unit;
      b.classList.toggle('is-active', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1;
    });
  }
  // Re-derive the three unit-bearing stats from the stored raw import values, so a unit
  // switch updates distance (km/mi), pace (/km-/mi) AND elevation (m/ft) together. Each
  // is left alone if the customer has manually edited it.
  function rederiveUnitFields() {
    var t = state.design.text, unit = t.distanceUnit;
    if (!state.edited.distance && state.routeMetres != null) { t.distance = formatDistanceWithUnit(state.routeMetres, unit); setInput('distance', t.distance); }
    if (!state.edited.averagePace && state.routeMovingSeconds > 0 && state.routeMetres > 0) { t.averagePace = formatPace(state.routeMovingSeconds, state.routeMetres, unit); setInput('averagePace', t.averagePace); }
    if (!state.edited.elevation && state.routeElevationGainMetres != null) { t.elevation = formatElevation(state.routeElevationGainMetres, unit); setInput('elevation', t.elevation); }
  }
  function selectUnit(unit) {
    if (state.design.text.distanceUnit === unit) return;
    state.design.text.distanceUnit = unit;
    setUnitUI(unit);
    rederiveUnitFields();
    hide(el.unitHint); // the customer has taken control of the units; the fail-soft notice is moot
    scheduleSave();
  }
  // Force the units to match a Strava import. Unlike selectUnit this also drops any prior
  // manual edit of the unit-bearing stats, so an imported route matches Strava exactly
  // (the caller then runs applyRouteDefaults to fill them in the new units).
  function applyStravaUnits(unitSystem) {
    var unit = unitSystem === 'imperial' ? 'miles' : 'kilometres';
    state.design.text.distanceUnit = unit;
    setUnitUI(unit);
    state.edited.distance = false;
    state.edited.averagePace = false;
    state.edited.elevation = false;
  }
  // Fail-soft units notice: a Strava import matches the athlete's own km/miles preference,
  // but that preference can only be read with the profile scope the athlete granted. When
  // it could not be resolved (unitSystem is null), we keep the current toggle rather than
  // guess, and tell the customer so a km runner is never silently shown miles. Cleared for
  // any non-Strava source, a successfully-resolved import, or once the customer toggles.
  function updateStravaUnitHint(st) {
    if (!el.unitHint) return;
    var resolved = st.unitSystem === 'imperial' || st.unitSystem === 'metric';
    if (st.sourceType === 'strava_activity' && !resolved) {
      var unitWord = state.design.text.distanceUnit === 'kilometres' ? 'kilometres' : 'miles';
      var other = unitWord === 'kilometres' ? 'miles' : 'kilometres';
      el.unitHint.textContent = 'We could not read your Strava distance units, so these stats are shown in ' + unitWord + '. Use the Units toggle to switch to ' + other + ' if that is not right.';
      show(el.unitHint);
    } else {
      hide(el.unitHint);
    }
  }
  function setInput(key, val) { var i = qs('[data-rps-text="' + key + '"]'); if (i && i.value !== val) i.value = val; }

  // Roving-tabindex + arrow-key navigation for an ARIA radiogroup.
  function wireRadioGroup(container, radioSel, selectFromEl) {
    if (!container) return;
    container.addEventListener('keydown', function (e) {
      if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].indexOf(e.key) === -1) return;
      var list = qsa(radioSel, container).filter(function (r) { return r.getAttribute('aria-disabled') !== 'true'; });
      if (!list.length) return;
      e.preventDefault();
      var cur = list.findIndex(function (r) { return r.tabIndex === 0; });
      if (cur < 0) cur = list.findIndex(function (r) { return r.getAttribute('aria-checked') === 'true'; });
      if (cur < 0) cur = 0;
      var to = cur;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (cur + 1) % list.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (cur - 1 + list.length) % list.length;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = list.length - 1;
      var target = list[to];
      selectFromEl(target);
      target.focus();
    });
  }

  // ---- summary + review --------------------------------------------------
  function updateSummary() {
    var p = state.design.product;
    el.sumSize.textContent = p.size + ' (' + SIZE_DIMS[p.size] + ')';
    el.sumPresentation.textContent = PRESENTATION_NAMES[p.presentation];
    el.sumStyle.textContent = STYLE_NAMES[state.design.styleId] || state.design.styleId;
    el.sumDigital.textContent = p.digitalCopy ? 'Added' : 'Not added';
    renderTotals();
  }

  function variantsExist() { return !!(product && product.variants && product.variants.length); }
  function digitalPrice() { return state.design.product.digitalCopy && typeof DIGITAL.price === 'number' ? DIGITAL.price : 0; }
  // { now, was } in pence. `was` is the full (compare-at / RRP) total, present only
  // when it beats `now` so the bar can strike it through to show the saving. The
  // print's compare-at drives the saving; the digital add-on (no compare-at) is added
  // at its real price to both sides.
  function priceParts() {
    var v = resolveVariant();
    if (!v || typeof v.price !== 'number') return null;
    var dig = digitalPrice();
    var now = v.price + dig;
    var rrp = (typeof v.compare_at_price === 'number' && v.compare_at_price > v.price) ? v.compare_at_price : v.price;
    var was = rrp + dig;
    return { now: now, was: was > now ? was : null };
  }
  // Render the subtotal: struck-through RRP (when there is a saving) + the live price.
  function renderTotals() {
    var parts = priceParts();
    var html;
    if (!parts) html = 'Confirmed at checkout';
    else if (parts.was) html = '<s class="rps__was">' + formatMoney(parts.was) + '</s> <span class="rps__now">' + formatMoney(parts.now) + '</span>';
    else html = formatMoney(parts.now);
    el.sumTotals.forEach(function (n) { if (n) n.innerHTML = html; }); // money strings only, no user input
    // Launch-discount pill: show only when the live config has a genuine saving (a struck
    // RRP beside the price), so the pill never claims a discount that is not on screen.
    if (el.launchPill) { if (parts && parts.was) show(el.launchPill); else hide(el.launchPill); }
    // Softly flash the subtotal when its value actually changes (never on first paint).
    if (state.lastTotalHtml !== undefined && state.lastTotalHtml !== html) {
      el.sumTotals.forEach(function (n) { restartAnim(n, 'rps-anim-pulse'); });
    }
    state.lastTotalHtml = html;
  }
  function formatMoney(cents) {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP' }).format(cents / 100); }
    catch (e) { return '£' + (cents / 100).toFixed(2); }
  }

  function renderReview() {
    var p = state.design.product, t = state.design.text;
    var rows = [
      ['Artwork style', STYLE_NAMES[state.design.styleId] || state.design.styleId]
    ];
    if (HEX6.test(state.design.routeColour || '')) rows.push(['Route line colour', state.design.routeColour.toUpperCase()]);
    rows = rows.concat([
      ['Title', t.title || 'Not set'],
      ['Subtitle', t.subtitle || 'Not set'],
      ['Date', t.date || 'Not set'],
      ['Print size', p.size + ' (' + SIZE_DIMS[p.size] + ')'],
      ['Presentation', PRESENTATION_NAMES[p.presentation]],
      ['Digital copy', p.digitalCopy ? 'Added' : 'Not added']
    ]);
    el.reviewList.innerHTML = rows.map(function (r) {
      return '<div class="rps__review-row"><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>';
    }).join('');
  }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  // ---- variant mapping + add to basket -----------------------------------
  function resolveVariant() {
    var variants = (product && product.variants) || [];
    if (!variants.length) return null;
    var size = state.design.product.size;
    var pres = state.design.product.presentation;
    var sizeRe = new RegExp('\\b' + size + '\\b', 'i');
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opts = [v.option1, v.option2, v.option3].filter(Boolean).map(String);
      var hasSize = opts.some(function (o) { return sizeRe.test(o); });
      var hasPres = opts.some(function (o) {
        return pres === 'framed' ? /fram/i.test(o) : /print/i.test(o);
      });
      if (hasSize && hasPres) return v;
    }
    if (variants.length === 1) return variants[0]; // single default variant (pre Phase 6), keep flow testable
    return null;
  }

  function canAddToCart() {
    return state.hasRoute && state.previewRendered && !state.previewFailed &&
      !!state.design.styleId && !!state.design.product.size && !!state.design.product.presentation && !!resolveVariant();
  }

  function updateGate() {
    // After a successful add the bar shows the "added / add another" state; don't let a
    // late save or preview event overwrite it with a step hint.
    if (state.addedToCart) { updatePrimaryAction(); return; }
    var hint;
    if (!state.hasRoute) hint = 'Add a route to continue.';
    else if (state.previewFailed) hint = 'We could not generate your preview. Use Try again on the preview.';
    else if (!state.previewRendered) hint = 'Generating your preview…';
    else if (!resolveVariant()) hint = variantsExist() ? 'This size and finish are not available. Please choose another.' : 'This print is not quite ready to buy. Please check back soon.';
    else hint = ''; // ready to buy: no hint text (just the Add to basket button)
    setAllText(el.ctaHints, hint);
    // Below the desktop breakpoint the cta-hint element is display:none, which left a
    // disabled buy button with no visible reason on phones. Mirror a non-empty hint
    // into the bar's full-width message line (neutral "info" style; a real add-to-cart
    // error set via setBarError replaces it and clears the info styling).
    if (el.barError) {
      var desktopHints = false;
      try { desktopHints = window.matchMedia('(min-width: 1024px)').matches; } catch (e) { /* mobile-first: mirror */ }
      if (!desktopHints) {
        if (hint) {
          el.barError.textContent = hint;
          el.barError.classList.add('is-info');
          el.barError.hidden = false;
        } else if (el.barError.classList.contains('is-info')) {
          el.barError.hidden = true;
          el.barError.classList.remove('is-info');
        }
      }
    }
    updatePrimaryAction();
  }

  // The single primary button (order summary + mobile bar) is step-aware: it advances
  // through the steps ("Next: …") on steps 1-4 and becomes "Add to basket" on Review.
  function updatePrimaryAction() {
    // Once a print is in the basket the bar's job is done: it becomes the "add a second
    // one at 50% off" prompt regardless of which step is showing behind the drawer.
    if (state.addedToCart) {
      el.primaries.forEach(function (b) { b.disabled = b.classList.contains('is-busy'); });
      setAllText(el.primaryLabels, 'Add another for 50% off');
      return;
    }
    var step = state.currentStep;
    var isReview = step === 'review';
    // Before a route exists the bar button is a route-starting action ("Add your
    // route", scrolls to the panel) rather than a disabled "Next": the bar is fixed,
    // so a customer deep in the page always has a live way back into the flow.
    var noRouteYet = step === 'route' && !state.hasRoute;
    var enabled = isReview ? canAddToCart() : true;
    var label = isReview ? 'Add to basket' : (noRouteYet ? 'Add your route' : (NEXT_LABELS[step] || 'Next'));
    el.primaries.forEach(function (b) { b.disabled = !enabled || b.classList.contains('is-busy'); });
    setAllText(el.primaryLabels, label);
  }
  function primaryAction() {
    if (state.addedToCart) { addAnother(); return; }
    if (state.currentStep === 'route' && !state.hasRoute) { scrollToRoutePanel(); return; }
    if (state.currentStep === 'review') { addToCart(); return; }
    var next = STEP_ORDER[STEP_ORDER.indexOf(state.currentStep) + 1];
    if (next) setStep(next);
  }

  // The bar's Back button (left): hidden on the first step, else goes to the previous step.
  function updateBackButton() {
    // Hidden on the first step, and once a print is in the basket (the flow is frozen).
    if (el.backGlobal) el.backGlobal.hidden = state.addedToCart || STEP_ORDER.indexOf(state.currentStep) <= 0;
  }

  // Visible 5-step progress indicator. Marks the current step (aria-current), the done
  // steps, and disables jumping to a later step before a route exists.
  function updateStepper() {
    if (!el.steps || !el.steps.length) return;
    var idx = STEP_ORDER.indexOf(state.currentStep);
    el.steps.forEach(function (b) {
      var i = STEP_ORDER.indexOf(b.dataset.rpsStep);
      var current = i === idx;
      b.classList.toggle('is-current', current);
      b.classList.toggle('is-done', i < idx);
      if (current) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
      // Before a route exists only "Route" is reachable; after, any step can be jumped to.
      // Once added, the flow is frozen (the drawer + "Add another" are the next actions).
      b.disabled = state.addingToCart || state.addedToCart || (!state.hasRoute && b.dataset.rpsStep !== 'route');
    });
  }

  function addToCart() {
    if (!canAddToCart()) return;
    var variant = resolveVariant();
    setCtaBusy(true);
    var lockData = null;
    var addedLineCount = 1; // print line, plus the digital add-on line when chosen
    flushDesignForCheckout()
      .then(function () {
        // Size/finish may have changed during the flush window; re-resolve so the signed
        // properties can never mismatch the cart line's variant.
        var current = resolveVariant();
        if (current && (!variant || current.id !== variant.id)) variant = current;
        if (!variant) throw new Error('That size and finish are not available. Please choose another.');
        return proxyFetch('/jobs/' + state.jobId + '/lock-for-cart', { method: 'POST', timeoutMs: 35000 });
      })
      .then(function (data) {
        lockData = data;
        // Merge the signed server properties with an optional gift message (a plain
        // line-item property; it does not affect the signature the webhook verifies).
        var printProps = {};
        for (var k in data.properties) { if (Object.prototype.hasOwnProperty.call(data.properties, k)) printProps[k] = data.properties[k]; }
        if (el.giftToggle && el.giftToggle.checked) {
          printProps['Gift'] = 'Yes';
          var gm = el.giftMessage && el.giftMessage.value ? el.giftMessage.value.trim() : '';
          if (gm) printProps['Gift message'] = gm.slice(0, 200);
        }
        var items = [{ id: variant.id, quantity: 1, properties: printProps }];
        // The digital copy is a separate paid add-on line (+£5). It MUST carry
        // _rps_digital_for=<jobId> (below) so the order webhook pairs it 1:1 with THIS
        // print and delivers exactly one digital copy. Do not remove that property: the
        // signed digitalCopy flag alone never unlocks a free copy (that was audit L4).
        if (state.design.product.digitalCopy && DIGITAL.variantId) {
          var digProps = { 'Digital copy for': (state.design.text.title || 'your route print') };
          // Pair this paid add-on line to its specific print job (hidden _ property), so
          // order fulfilment delivers exactly one digital copy per paid add-on, tied to
          // the right route. Without this a multi-print order could leak free digital
          // copies. Same public job id already on the print line.
          digProps['_rps_digital_for'] = state.jobId;
          // Carry the SAME custom preview image as the print line so the digital
          // line shows the customer's design too (not the blank add-on product image).
          if (data.properties && data.properties['_rps_preview']) digProps['_rps_preview'] = data.properties['_rps_preview'];
          items.push({ id: DIGITAL.variantId, quantity: 1, properties: digProps });
        }
        addedLineCount = items.length;
        var sig = printProps['_route_print_job_signature'];
        var rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        // Ask the add call to also return the freshly rendered cart-drawer section HTML so
        // we can morph + open the drawer (like every other product) instead of hard-
        // redirecting to /checkout. The drawer component reports its own section id when we
        // dispatch the theme's cart:grouped-sections event; if no drawer is present the
        // list is simply empty and the add still succeeds.
        var sectionIds = [];
        try { document.dispatchEvent(new CustomEvent('cart:grouped-sections', { detail: { sections: sectionIds } })); } catch (e) { /* no drawer on page */ }
        sectionIds = sectionIds.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
        var addBody = { items: items };
        if (sectionIds.length) { addBody.sections = sectionIds.join(','); addBody.sections_url = window.location.pathname; }
        var doAdd = function () {
          // Deadline the cart add like every other network call, so a hung request can
          // never wedge the button on "Adding…" with no recovery (it surfaces the same
          // retryable bar error instead).
          var ctrl = ('AbortController' in window) ? new AbortController() : null;
          var to = ctrl ? setTimeout(function () { ctrl.abort(); }, 25000) : null;
          return fetch(rootPath + 'cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addBody),
            signal: ctrl ? ctrl.signal : undefined
          }).then(function (r) {
            if (to) clearTimeout(to);
            return r.json().catch(function () { return {}; }).then(function (j) {
              if (!r.ok) throw new Error(j.description || j.message || 'Could not add to basket. Please try again.');
              return j;
            });
          }, function (err) {
            if (to) clearTimeout(to);
            throw new Error(err && err.name === 'AbortError' ? 'That took too long. Please check your connection and try again.' : ((err && err.message) || 'Could not add to basket. Please try again.'));
          });
        };
        // IDEMPOTENCY: a prior add of this exact signed line may have succeeded on the
        // server while its response was lost (flaky mobile), leaving the button re-enabled.
        // Re-adding the byte-identical signed line would merge it to quantity 2 in Shopify
        // = a DOUBLE CHARGE (one file delivered). So first check whether the signature is
        // already in the cart; if so, treat it as added and skip the duplicate add. The
        // signature is deterministic per job, so a retry always matches the earlier line.
        // Fail-open: if the cart lookup itself fails, fall through to the normal add.
        return fetch(rootPath + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r && r.ok ? r.json() : null; }, function () { return null; })
          .catch(function () { return null; })
          .then(function (cart) {
            var already = cart && Array.isArray(cart.items) && cart.items.some(function (it) {
              return it && it.properties && it.properties['_route_print_job_signature'] === sig;
            });
            if (already) return cart; // already in the cart from a prior (lost-response) add
            return doAdd();
          });
      })
      .then(function (cartData) {
        // The line just added is signed against the CURRENT job, which is now frozen.
        // Switch this page session to the backend's continuation clone so a back-
        // navigation (bfcache) edit or a second add can never invalidate that line.
        if (lockData && lockData.continuation && lockData.continuation.publicId) {
          setJobId(lockData.continuation.publicId);
        }
        // Open the cart drawer (like every other product) rather than jumping straight
        // to checkout, so the customer can add a second print at 50% off first.
        onAddedToCart(addedLineCount, cartData);
      })
      .catch(function (e) {
        setCtaBusy(false);
        var m = (e && e.message) || 'Could not add to basket. Please try again.';
        setAllText(el.ctaHints, m); // desktop hint
        setBarError(m);             // visible on ALL breakpoints (mobile buy failures were silent)
      });
  }
  function setBarError(msg) { if (el.barError) { el.barError.classList.remove('is-info'); el.barError.setAttribute('aria-live', 'assertive'); el.barError.textContent = msg; el.barError.hidden = false; } }
  function setBarInfo(msg) { if (el.barError) { el.barError.classList.add('is-info'); el.barError.setAttribute('aria-live', 'polite'); el.barError.textContent = msg; el.barError.hidden = false; } }
  function clearBarError() { if (el.barError) el.barError.hidden = true; }

  // Opens the theme's own cart drawer (falling back to the header cart control, then the
  // cart page) so the "added" moment matches every other product on the store.
  function openCartDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && typeof drawer.showDialog === 'function') { try { drawer.showDialog(); return; } catch (e) { /* fall through */ } }
    var btn = document.querySelector('.cart-drawer-button');
    if (btn) { try { btn.click(); return; } catch (e) { /* fall through */ } }
    window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.cart_url) || ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart';
  }

  // Called after a successful add. Tells the theme the cart changed (updates the header
  // count + drawer contents and auto-opens the drawer), then flips the studio into its
  // post-add state where the primary action becomes "Add another for 50% off".
  function onAddedToCart(lineCount, cartData) {
    state.addedToCart = true;
    state.addingToCart = false;
    // The print is now in the Shopify cart (server-side). Drop the resume session so a
    // refresh in the post-add state starts fresh rather than re-opening the continuation
    // clone; the cart still holds their item, and "Add another" reloads to a clean studio.
    clearSession();
    var sections = cartData && cartData.sections ? cartData.sections : undefined;
    var delta = lineCount > 0 ? lineCount : 1;
    // Mirror the theme's CartAddEvent: same event name (cart:update), incremental count,
    // and the rendered sections so the drawer morphs to the new contents before opening.
    var dispatched = false;
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData || {},
          sourceId: 'route-print-studio',
          data: { source: 'route-print-studio', itemCount: delta, isIncremental: true, sections: sections }
        }
      }));
      dispatched = true;
    } catch (e) { /* dispatch failed: open the drawer directly below */ }
    // Nudge the drawer open even if it did not auto-open (e.g. auto-open disabled), and
    // when we could not dispatch at all.
    if (!dispatched) openCartDrawer();
    else setTimeout(function () { var dlg = document.getElementById('cart-drawer-dialog'); if (!dlg || !dlg.open) openCartDrawer(); }, 80);
    // Post-add bar state: confirmation + "Add another for 50% off".
    el.primaries.forEach(function (b) { b.classList.remove('is-busy'); });
    if (el.backGlobal) { el.backGlobal.disabled = false; el.backGlobal.hidden = true; }
    setBarInfo(ADDED_TO_CART_MSG);
    updatePrimaryAction();
    updateStepper();
  }

  // "Add another for 50% off": restart the studio for a second print. The first print is
  // already in the basket server-side and the offer applies automatically at checkout, so
  // a clean reload (fresh job, step 1, default design) is the safest, race-free reset.
  function addAnother() {
    window.location.href = window.location.origin + window.location.pathname;
  }
  function setCtaBusy(busy) {
    if (busy) clearBarError(); // a fresh attempt clears the last failure message
    el.primaries.forEach(function (b) { b.classList.toggle('is-busy', busy); b.disabled = busy; });
    // Freeze Back while an add-to-cart is in flight: stepping back to change size/finish
    // mid-add would race the signed cart line. Re-enabled (via updateBackButton) after.
    if (el.backGlobal) el.backGlobal.disabled = busy;
    state.addingToCart = busy;
    if (busy) setAllText(el.primaryLabels, 'Adding…');
    else { updatePrimaryAction(); updateBackButton(); }
  }

  // ---- step navigation ---------------------------------------------------
  function setStep(step, opts) {
    if (STEP_ORDER.indexOf(step) === -1) return;
    if (state.addingToCart || state.addedToCart) return; // frozen during/after the cart add
    if (!state.hasRoute && step !== 'route') { setStep('route'); showRouteNote('Add your route first, then you can move on.'); return; }
    state.currentStep = step;
    STEP_ORDER.forEach(function (s) {
      var active = s === step;
      el.panels[s].hidden = !active;
      el.panels[s].classList.toggle('is-active', active);
    });
    if (step === 'review') renderReview();
    updatePrimaryAction(); // the bar's primary button reflects the new step
    updateBackButton();    // and the bar's Back button (hidden on the first step)
    updateStepper();       // reflect the current step in the visible progress indicator
    var panel = el.panels[step];
    // preventScroll on request: the auto-advance after an import wants to land on the
    // preview (money shot), not the newly-focused panel.
    var noScroll = !!(opts && opts.scroll === false);
    if (panel && typeof panel.focus === 'function') panel.focus({ preventScroll: noScroll });
  }

  // ---- helpers -----------------------------------------------------------
  function showRouteError(msg) { hide(el.routeNote); el.routeError.textContent = msg; show(el.routeError); state.applyingRoute = false; hideOverlay(); }
  function showRouteNote(msg) { hide(el.routeError); el.routeNote.textContent = msg; show(el.routeNote); }
  function setGpxStatus(msg, ok) { el.gpxStatus.textContent = msg; el.gpxStatus.classList.toggle('is-set', !!ok); }

  // Full-page connecting overlay (Strava connect + activity load).
  var overlayBg = [qs('.rps__shell'), el.bar].filter(Boolean);
  function setOverlayInert(on) {
    overlayBg.forEach(function (n) {
      if (on) { try { n.setAttribute('inert', ''); } catch (e) {} n.setAttribute('aria-hidden', 'true'); }
      else { try { n.removeAttribute('inert'); } catch (e) {} n.removeAttribute('aria-hidden'); }
    });
  }
  function showOverlay(msg) {
    if (!el.overlay) return;
    if (el.overlayText && msg) el.overlayText.textContent = msg;
    el.overlay.hidden = false;
    // Behave like a modal: keep the background out of the tab order + off the a11y tree
    // while the full-page veil is up, so focus cannot wander behind it.
    setOverlayInert(true);
  }
  function hideOverlay() {
    if (!el.overlay) return;
    el.overlay.hidden = true;
    setOverlayInert(false);
  }
  // Centered "Applying route…" overlay shown the moment any route is chosen (GPX,
  // popular or Strava), dismissed once the new preview renders (or on failure). It
  // ALSO clears the current preview + invalidates any in-flight render right here on
  // CLICK, so if the import then fails, lifting the overlay reveals the empty/loading
  // stage (and the error), never the previously-selected route's image.
  // Failsafe watchdog: if anything ever leaves the full-page "Applying route…" veil up
  // (a code path that misses its catch, a promise that never settles), lift it and show
  // a retryable error rather than a frozen page. The per-request timeouts should always
  // fire first; this is the backstop.
  var applyingWatchdog = null;
  function startApplyingRoute() {
    state.applyingRoute = true; showOverlay('Applying route…'); resetPreviewForNewRoute();
    if (applyingWatchdog) clearTimeout(applyingWatchdog);
    applyingWatchdog = setTimeout(function () {
      applyingWatchdog = null;
      if (!state.applyingRoute) return;
      stopApplyingRoute();
      showRouteError('That took too long to apply. Check your connection and try again.');
    }, 70000);
  }
  function stopApplyingRoute() {
    if (applyingWatchdog) { clearTimeout(applyingWatchdog); applyingWatchdog = null; }
    if (state.applyingRoute) { state.applyingRoute = false; hideOverlay(); }
  }
  // Anchor-scroll helpers. The preview is the customer's "money shot"; the picker
  // is where they choose. scroll-margin-top (CSS) clears the sticky theme header.
  // ---- Zoom lightbox: an enlarged view of just the print ------------------
  // Available only once a real preview has rendered (an example / loading / blank
  // stage is never zoomable). setZoomable drives the on-poster "Click to zoom" cue.
  var zoomLastFocus = null;
  function zoomAvailable() {
    return !!(state.previewRendered && el.previewImg && el.previewImg.getAttribute('src') && !el.previewImg.hidden);
  }
  function setZoomable(on) { if (el.previewStage) el.previewStage.classList.toggle('is-zoomable', !!on && zoomAvailable()); }
  function openZoom() {
    if (!el.zoom || !el.zoomImg || !el.zoom.hidden || !zoomAvailable()) return;
    el.zoomImg.src = el.previewImg.currentSrc || el.previewImg.src;
    el.zoomImg.alt = el.previewImg.alt || 'Enlarged preview of your print.';
    zoomLastFocus = document.activeElement;
    el.zoom.hidden = false;
    document.body.classList.add('rps-zoom-open');
    document.addEventListener('keydown', onZoomKeydown, true);
    var closeBtn = qs('[data-rps-zoom-close]', el.zoom);
    if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
  }
  function closeZoom() {
    if (!el.zoom || el.zoom.hidden) return;
    el.zoom.hidden = true;
    document.body.classList.remove('rps-zoom-open');
    document.removeEventListener('keydown', onZoomKeydown, true);
    if (el.zoomImg) el.zoomImg.removeAttribute('src'); // release the large image
    if (zoomLastFocus && typeof zoomLastFocus.focus === 'function') { try { zoomLastFocus.focus(); } catch (e) {} }
    zoomLastFocus = null;
  }
  function onZoomKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); closeZoom(); return; }
    // The dialog has a single focusable control (Close), so keep focus pinned to it.
    if (e.key === 'Tab') { e.preventDefault(); var b = qs('[data-rps-zoom-close]', el.zoom); if (b) b.focus(); }
  }
  function bindZoom() {
    // Portal the lightbox to <body> so it is not trapped in the section's stacking
    // context. Left in place, its z-index is local and the site header (a lower z-index
    // at document root) paints OVER it, clipping the top of a tall poster. At body level
    // the overlay covers the whole viewport, header included, as a proper modal. Its CSS
    // is class-based (not scoped under .rps) and box-sizing is border-box globally, so
    // nothing about its styling changes. Guarded so a section re-render cannot double-move.
    if (el.zoom && el.zoom.parentNode !== document.body) document.body.appendChild(el.zoom);
    qsa('[data-rps-zoom-open]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); openZoom(); });
    });
    // Clicking the rendered print (not the loading veil / example) also opens the zoom.
    if (el.poster) el.poster.addEventListener('click', function () { if (zoomAvailable()) openZoom(); });
    if (el.zoom) el.zoom.addEventListener('click', function (e) {
      // Clicking the print keeps the view open; anything else (backdrop, close, hint) shuts it.
      if (e.target === el.zoomImg) return;
      closeZoom();
    });
  }

  function scrollToPreview() { try { (el.previewCol || el.previewStage).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }
  function scrollToStylePanel() { try { el.panels.style.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }
  function scrollToRoutePanel() { try { el.panels.route.scrollIntoView({ behavior: 'smooth', block: 'start' }); el.panels.route.focus({ preventScroll: true }); } catch (e) {} }
  function scrollToStravaPicker() { try { (el.stravaPanel || el.previewStage).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }
  function scrollToPopularPicker() { try { (el.popularPanel || el.previewStage).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }
  function scrollToDrawPicker() { try { (el.drawPanel || el.previewStage).scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }
  // On mobile the style picker sits below the preview, so a fresh style change happens
  // off-screen. Bring the customer back up to the preview so they watch it update.
  // (Desktop shows preview + picker side by side, so no scroll is needed there.)
  function isMobileLayout() { try { return window.matchMedia('(max-width: 680px)').matches; } catch (e) { return false; } }
  // Only the FIRST style change of the session bounces the viewport up to the preview;
  // later taps stay put so a customer can compare styles without the page jumping each time.
  function scrollToPreviewOnMobile() {
    if (!isMobileLayout() || state.styleScrolled) return;
    state.styleScrolled = true;
    scrollToPreview();
  }
  // Restart a one-shot CSS animation by re-adding its class (with a forced reflow), so a
  // keyframe keyed on a class replays even when nothing else about the element changed,
  // e.g. the preview image src swapping in place on a style/text re-render, or the stage
  // cross-dissolving on a view toggle. No-op visually under reduced motion (the keyframe
  // rule is gated to no-preference), and the class is harmless if it lingers.
  function restartAnim(node, cls) {
    if (!node) return;
    node.classList.remove(cls);
    void node.offsetWidth; // reflow so re-adding the class restarts the animation
    node.classList.add(cls);
  }

  function debounce(fn, ms) { var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }
  // Move keyboard + screen-reader focus into a just-revealed route picker (its heading), so a
  // keyboard user is not left tabbing blindly from the source button. preventScroll keeps it
  // from fighting the smooth scrollIntoView; the heading is not a normal tab stop, hence -1.
  function focusPanel(panel) {
    if (!panel) return;
    var h = panel.querySelector('.rps__strava-title') || panel;
    try { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); } catch (e) {}
  }
  // Reflect which route-source disclosure (Popular / Draw) is open on its trigger button.
  function syncSourceExpanded() {
    var open = { popular: !!(el.popularPanel && !el.popularPanel.hidden), draw: !!(el.drawPanel && !el.drawPanel.hidden) };
    qsa('[data-rps-source]').forEach(function (b) {
      var s = b.dataset.rpsSource;
      if (s in open) b.setAttribute('aria-expanded', open[s] ? 'true' : 'false');
    });
  }

  // Light-touch deterrent against casually saving the preview artwork (right-click,
  // drag-to-desktop, iOS long-press). Not real DRM, the print is delivered on order.
  function guardPreviewImage() {
    if (!el.previewImg) return;
    el.previewImg.setAttribute('draggable', 'false');
    el.previewImg.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    el.previewImg.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  // ---- init --------------------------------------------------------------
  function init() {
    // Lock the map styles; preselect the working defaults.
    qsa('[data-rps-style]').forEach(function (b) {
      if (LOCKED.has(b.dataset.rpsStyle)) {
        b.classList.add('is-locked');
        b.setAttribute('aria-disabled', 'true');
        var soon = qs('[data-rps-style-soon]', b); if (soon) soon.hidden = false;
      }
    });
    selectStyle(DEFAULT_STYLE);
    selectSize('A4');
    selectPresentation('print-only');
    updateSummary();
    updateGate();

    // Panel nav: the single primary button (summary / mobile bar) drives the steps forward;
    // the global Back button steps backwards.
    if (el.backGlobal) el.backGlobal.addEventListener('click', function () {
      var prev = STEP_ORDER[STEP_ORDER.indexOf(state.currentStep) - 1];
      if (prev) setStep(prev);
    });
    updateBackButton();
    // Stepper: each step is a jump link (setStep guards the no-route and busy cases).
    el.steps.forEach(function (b) { b.addEventListener('click', function () { setStep(b.dataset.rpsStep); }); });
    updateStepper();

    // Route sources. Strava is live when enabled; otherwise polite guidance.
    qsa('[data-rps-source]').forEach(function (b) {
      b.addEventListener('click', function () {
        var src = b.dataset.rpsSource;
        if (src === 'popular') { openPopularPicker(); return; }
        if (src === 'draw') { openDrawPicker(); return; }
        if (src === 'strava') {
          if (STRAVA_ENABLED) { connectStrava(); return; }
          showRouteNote('Strava import is arriving soon. For now, please upload a GPX file below.');
        }
      });
    });
    if (el.stravaReconnect) el.stravaReconnect.addEventListener('click', connectStrava);
    // Draw your own route
    if (el.drawLocate) el.drawLocate.addEventListener('click', drawLocate);
    if (el.drawAdd) el.drawAdd.addEventListener('click', addDrawPinAtCentre);
    if (el.drawUndo) el.drawUndo.addEventListener('click', undoDrawPin);
    if (el.drawClear) el.drawClear.addEventListener('click', clearDraw);
    if (el.drawUse) el.drawUse.addEventListener('click', useDrawnRoute);
    if (el.drawRetry) el.drawRetry.addEventListener('click', function () { hideDrawError(); runDrawSnap(); });
    var applyPopularDebounced = debounce(applyPopularView, 150);
    if (el.popularSearch) el.popularSearch.addEventListener('input', function () { state.popularSearch = el.popularSearch.value; applyPopularDebounced(); });
    var applyActivityDebounced = debounce(applyActivityView, 150);
    if (el.stravaSearch) el.stravaSearch.addEventListener('input', function () { state.activitySearch = el.stravaSearch.value; applyActivityDebounced(); });
    if (el.stravaSort) el.stravaSort.addEventListener('change', function () { state.activitySort = el.stravaSort.value; applyActivityView(); });
    if (el.stravaFilters) qsa('[data-rps-activity-filter]', el.stravaFilters).forEach(function (chip) {
      chip.addEventListener('click', function () {
        state.activityFilter = chip.dataset.rpsActivityFilter;
        qsa('[data-rps-activity-filter]', el.stravaFilters).forEach(function (c) {
          var on = c === chip;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        applyActivityView();
      });
    });

    // GPX
    el.gpxInput.addEventListener('change', function () {
      var f = el.gpxInput.files && el.gpxInput.files[0];
      // Reset immediately: browsers do not fire `change` when the SAME file is picked
      // again, which made "upload failed -> try the same file" a silent dead end.
      el.gpxInput.value = '';
      onGpxSelected(f);
    });
    if (el.dropzone) {
      ['dragover', 'dragenter'].forEach(function (ev) { el.dropzone.addEventListener(ev, function (e) { e.preventDefault(); el.dropzone.classList.add('is-dragover'); }); });
      ['dragleave', 'drop'].forEach(function (ev) { el.dropzone.addEventListener(ev, function (e) { e.preventDefault(); el.dropzone.classList.remove('is-dragover'); }); });
      el.dropzone.addEventListener('drop', function (e) { if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) onGpxSelected(e.dataTransfer.files[0]); });
    }
    var help = qs('[data-rps-gpx-help]'); var helpPanel = qs('[data-rps-gpx-help-panel]');
    if (help && helpPanel) help.addEventListener('click', function () { var open = helpPanel.hidden; helpPanel.hidden = !open; help.setAttribute('aria-expanded', open ? 'true' : 'false'); });

    // Styles. On mobile, scroll up to the preview so the customer sees it update.
    qsa('[data-rps-style]').forEach(function (b) { b.addEventListener('click', function () { selectStyle(b.dataset.rpsStyle); scrollToPreviewOnMobile(); }); });
    wireRadioGroup(qs('[data-rps-styles]'), '[data-rps-style]', function (t) { selectStyle(t.dataset.rpsStyle); scrollToPreviewOnMobile(); });

    // bfcache: Safari/Chrome restore this page from the back/forward cache (state
    // intact, in-flight fetches dead) when the customer taps Back from checkout or
    // from the Strava authorise screen. Clear every transient busy state so the
    // restored page is interactive, never wedged on "Adding…" or a full-page veil.
    window.addEventListener('pageshow', function (e) {
      if (!e || !e.persisted) return;
      setCtaBusy(false);
      stopApplyingRoute();
      hideOverlay();
      clearBarError();
      if (state.addedToCart) setBarInfo(ADDED_TO_CART_MSG); // keep the post-add confirmation after a Back
      // A bfcache restore can freeze a mid-flight save/preview chain; reset the internal
      // serialisation locks too (not just the visible UI) and orphan any zombie chain,
      // otherwise design saves and preview updates could silently stop after Back.
      saveGen++;
      state.saving = false;
      state.pendingSave = false;
      state.patchInFlight = 0;
      if (state.hasRoute) scheduleSave(true, false); // reconcile the server design (no needless preview re-render; the cached image is still correct)
      updateGate();
    });

    // On-screen keyboard: the fixed bottom bar rode up over focused text inputs on
    // Android (the keyboard shrinks the viewport and the bar lands on the field).
    // While a studio text input has focus on a narrow layout, slide the bar away.
    // Gate on the SAME breakpoint the CSS hide rule uses (max-width:1023.98px), not the
    // narrower isMobileLayout() (680px): otherwise on 681-1023px devices the class was
    // never added and the bar could cover the focused field.
    var isKbdLayout = function () { try { return window.matchMedia('(max-width: 1023.98px)').matches; } catch (e) { return false; } };
    root.addEventListener('focusin', function (e) {
      if (!el.bar || !isKbdLayout()) return;
      var t = e.target;
      var isTextField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') &&
        ['radio', 'checkbox', 'file', 'color', 'range'].indexOf(t.type) === -1;
      if (isTextField) el.bar.classList.add('is-kbd');
    });
    root.addEventListener('focusout', function () {
      if (!el.bar) return;
      setTimeout(function () {
        var a = document.activeElement;
        var stillText = a && root.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA') &&
          ['radio', 'checkbox', 'file', 'color', 'range'].indexOf(a.type) === -1;
        if (!stillText) el.bar.classList.remove('is-kbd');
      }, 80);
    });
    // Android's "hide keyboard" button dismisses the keyboard WITHOUT blurring the input,
    // so focusout never fires and the bar (the only Next/Add control) stays translated
    // off-screen. Reconcile on the visual viewport instead: when it grows back toward its
    // full height the keyboard has closed, so drop is-kbd regardless of focus.
    if (window.visualViewport && el.bar) {
      var vv = window.visualViewport;
      var maxVh = vv.height;
      vv.addEventListener('resize', function () {
        if (vv.height > maxVh) maxVh = vv.height; // track the "no keyboard" height
        if (vv.height > maxVh - 120) el.bar.classList.remove('is-kbd'); // grew back => keyboard gone
      });
    }

    // Style gallery cards (the marketing section further down the page) carry
    // href="#rps-style-<id>": preselect that style in the builder, then bring the
    // customer back into the flow. With a route: jump to the Style step and show the
    // preview updating. Without one: back to Add your route, style remembered.
    document.addEventListener('click', function (ev) {
      var a = ev.target && ev.target.closest ? ev.target.closest('a[href^="#rps-style-"]') : null;
      if (!a) return;
      ev.preventDefault();
      var id = (a.getAttribute('href') || '').slice(11); // after "#rps-style-"
      if (qs('[data-rps-style="' + id + '"]')) selectStyle(id);
      if (state.hasRoute) { setStep('style'); scrollToPreview(); }
      else { scrollToRoutePanel(); showRouteNote('Style saved. Add your route to see it in this look.'); }
    });

    // Custom route line colour. Live-update the value label as they drag (input),
    // commit the colour + re-render the preview when they settle (change).
    if (el.colourInput) {
      el.colourInput.addEventListener('input', function () { if (el.colourValue) el.colourValue.textContent = (el.colourInput.value || '').toUpperCase(); });
      el.colourInput.addEventListener('change', function () { selectRouteColour(el.colourInput.value); });
    }
    if (el.colourReset) el.colourReset.addEventListener('click', resetRouteColour);

    // Text fields
    qsa('[data-rps-text]').forEach(function (input) {
      input.addEventListener('input', function () {
        var key = input.dataset.rpsText;
        state.design.text[key] = input.value;
        state.edited[key] = true; // a manual edit: keep it when the customer switches activity
        scheduleSave();
      });
    });
    qsa('[data-rps-unit]').forEach(function (b) { b.addEventListener('click', function () { selectUnit(b.dataset.rpsUnit); }); });
    wireRadioGroup(qs('[data-rps-units]'), '[data-rps-unit]', function (t) { selectUnit(t.dataset.rpsUnit); });

    // Size / presentation / digital
    qsa('[data-rps-size]').forEach(function (b) { b.addEventListener('click', function () { selectSize(b.dataset.rpsSize); }); });
    wireRadioGroup(qs('[data-rps-sizes]'), '[data-rps-size]', function (t) { selectSize(t.dataset.rpsSize); });
    qsa('[data-rps-presentation]').forEach(function (b) { b.addEventListener('click', function () { selectPresentation(b.dataset.rpsPresentation); }); });
    wireRadioGroup(qs('[data-rps-presentations]'), '[data-rps-presentation]', function (t) { selectPresentation(t.dataset.rpsPresentation); });
    var digital = qs('[data-rps-digital]');
    // Gate the add-on on the digital product actually being buyable. all_products only
    // returns products on the Online Store channel, so if route-print-studio-digital is
    // unpublished / handle-changed / removed, DIGITAL.variantId is 0. Without this gate the
    // checkbox stayed tick-able and the summary said "Added", but no paid line was created
    // and no copy delivered: the UI promised a paid deliverable that never happened. Hide
    // the whole row when it cannot be purchased so the customer never selects it.
    var digitalAvailable = !!(DIGITAL.variantId && DIGITAL.price);
    if (digital && digitalAvailable) {
      digital.addEventListener('change', function () { state.design.product.digitalCopy = digital.checked; updateSummary(); scheduleSave(false, false); }); // digital copy is a product option: no preview re-render
    } else {
      state.design.product.digitalCopy = false;
      if (digital) { digital.checked = false; digital.disabled = true; }
      var digitalRow = qs('[data-rps-digital-row]');
      if (digitalRow) digitalRow.hidden = true;
    }
    // Gift facility: reveal the message field when "This is a gift" is ticked.
    if (el.giftToggle && el.giftFields) {
      el.giftToggle.addEventListener('change', function () {
        el.giftFields.hidden = !el.giftToggle.checked;
        if (el.giftToggle.checked && el.giftMessage) { try { el.giftMessage.focus(); } catch (e) {} }
      });
    }
    // Show the add-on price (+£5) on the label, driven by the real add-on variant price.
    if (el.addonPrice && typeof DIGITAL.price === 'number' && DIGITAL.price > 0) {
      el.addonPrice.textContent = ' +' + formatMoney(DIGITAL.price);
      el.addonPrice.hidden = false;
    }

    // Preview views (artwork / on a wall) + retry
    qsa('[data-rps-view]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.classList.contains('is-active')) return; // already showing this view: nothing to swap
        qsa('[data-rps-view]').forEach(function (x) { x.classList.toggle('is-active', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        el.previewStage.classList.toggle('is-wall', b.dataset.rpsView === 'wall');
        restartAnim(el.previewStage, 'rps-anim-swap'); // cross-dissolve the stage into the new view
      });
    });
    if (el.previewRetry) el.previewRetry.addEventListener('click', function () {
      hidePreviewError();
      if (!state.hasRoute) {
        // The failure was an IMPORT (no route yet), so a save/render retry has nothing
        // to run against: restore the example poster and invite a fresh start instead
        // of leaving a blank stage.
        hide(el.posterLoading);
        setZoomable(false);
        show(el.posterExample); show(el.posterEmpty);
        showRouteNote('Choose a route source above to try again.');
        return;
      }
      state.previewAttempt = 0;
      scheduleSave(true);
    });
    guardPreviewImage(); // deter casual saving of the preview artwork
    bindZoom();          // "Click to zoom" cue + full-screen lightbox of the print

    // Single step-aware primary action (order summary + mobile bar): advances the
    // steps and, on Review, adds to basket.
    el.primaries.forEach(function (b) { b.addEventListener('click', function () { if (!b.disabled) primaryAction(); }); });

    // Keep the shell's bottom clearance tracking the fixed bar's ACTUAL height, so a
    // 2-3 line bar message on a small phone never hides the bottom of the step content.
    if (window.ResizeObserver && el.bar) {
      try {
        var barRO = new ResizeObserver(function () { var h = el.bar.offsetHeight; if (h) root.style.setProperty('--rps-bar-h', Math.ceil(h) + 'px'); });
        barRO.observe(el.bar);
      } catch (e) { /* older browsers keep the CSS fallback clearance */ }
    }

    // If we have just come back from the Strava OAuth round-trip, pick it up; otherwise try
    // to resume a still-in-progress job after an accidental refresh (guarded inside).
    handleStravaReturn();
    tryRestoreSession();
  }

  init();
})();
